RivalIQ.apps.Dashboard.module('widgets', function (widgets, App, Backbone, Marionette, $, _) {

    'use strict';

    const MAX_VISUAL_EXPORT_ROWS = 50;

    const COLUMN_MAX = {};
    COLUMN_MAX[RivalIQ.enums.orientation.PORTRAIT] = {
        6: 5,
        12: 9
    };

    COLUMN_MAX[RivalIQ.enums.orientation.LANDSCAPE] = {
        6: 9,
        12: 9
    };

    const ROW_ID_EXPANSION_PREFIX = 'id:';
    const SUMMARY_ROW = '__SUMMARY_ROW__';

    function sortRow(sortColumns, sortOrder, sortFunctions) {
        return function (a, b) {

            function sortByColumn(sortColumn) {
                const valueA = _.last(a[sortColumn]);
                const valueB = _.last(b[sortColumn]);

                return sortFunctions[sortColumn](valueA, valueB, sortOrder);
            }

            let idx = 0;
            let result = 0;
            while (_.exists(sortColumns[idx]) && result === 0) {
                result = sortByColumn(sortColumns[idx]);
                idx++;
            }

            return result;
        };
    }

    // The `rowExpansions` prop is interpreted as a comma separated list where each element is either:
    // - a row index
    // - an id (if the element starts with ROW_ID_EXPANSION_PREFIX), where a row is expanded if the id of that row matches.
    function getExpansions(propable) {
        const rowExpansions = (propable.getProp('rowExpansions') || '');
        const sanitized = rowExpansions.substring(1, rowExpansions.length - 1).split('.');

        return _.reduce(sanitized, function (memo, rowExpansion) {
            let index;
            if (_.startsWith(rowExpansion, ROW_ID_EXPANSION_PREFIX)) {
                const rowId = window.unescape(rowExpansion.substring(ROW_ID_EXPANSION_PREFIX.length));
                index = _.findIndex(propable.rows, function (element) {
                    return element[0][0] === rowId;
                });
            } else {
                index = parseInt(rowExpansion);
            }

            return (_.isFinite(index) ? memo.concat(index) : memo);
        }, []);
    }

    const cellTemplate = _.template('<td class="<%- className %>"><div><%= value %></div></td>');
    const cellTemplateExpandCollapse = _.template('<td class="<%- className %> expandable-table-cell" style="position: relative;"><i class="fa fa-fw fa-chevron-right table-expander x-export-hide pointer" data-toggle="tooltip" data-container="body" title="Expand"/><i class="fa fa-fw fa-chevron-down table-expander x-export-hide pointer" data-toggle="tooltip" data-container="body" title="Collapse"/><div><%= value %></div></td>');

    const getCellTemplate = function (view, columnIndex) {
        let fn = cellTemplate;
        if (columnIndex === 0 && view.isTableExpandable()) {
            fn = cellTemplateExpandCollapse;
        }
        return fn;
    };

    function isDisallowedCompanySetAggregation(dashboardModel) {
        return dashboardModel.getProp('primaryDimension') === 'companySetId' && !RivalIQ.state.plan.get('has_multi_handle_private_advanced');
    }

    widgets.MetricTable = RivalIQ.views.LayoutView.mixin({
        template: '#metricTableTemplate',

        displayMetricCellViews: true,

        mixins: {
            props: Marionette.Dashboard.mixins.Props,
            companySets: RivalIQ.apps.Shell.mixins.CompanySetEventsMixin
        },

        behaviors: {
            AffixedTableHeader: {},
            Resizes: {
                handler: 'checkEllipsibleContentForOverflow',
                behaviorClass: RivalIQ.graph.behaviors.Resizes
            }
        },

        isTableExpandable: function () {
            const me = this;

            return me.getProp('dimension') && (me.dashboardModel.isFocus() || me.dashboardModel.getCuration().isCustomDashboard);
        },

        className: function () {
            const me = this;
            const dashboardModel = me.dashboardModel;
            return [
                'chart table-chart',
                me.getProp('trailingBar') ? 'trailing-bar' : '',
                (me.isTableExpandable()) ? 'table-expandable' : '',
                dashboardModel.getSize(),
                dashboardModel.isExporting() ? 'x-table-container' : 'table-responsive'
            ].join(' ');
        },

        emptyView: Marionette.ItemView.extend({
            template: '#metricTableEmptyTemplate'
        }),

        getEmptyView: function () {
            const me = this;

            if (me.getProp('primaryDimension') === 'companySetId' && _.isEmpty(RivalIQ.state.companySets.getUserCompanySets())) {
                return Marionette.ItemView.extend({
                    template: '#metricTableNoCompanySetsEmptyTemplate'
                });
            }

            return Marionette.CollectionView.prototype.getEmptyView.call(me);
        },

        ui: {
            headerRow: '.x-metric-table > thead > tr',
            tbody: '.x-metric-table > tbody'
        },

        eventsNotToDebounce: {
            'click .x-sort': 'onSortClick',
            'click .x-data-row': '_onRowClick',
            'click .table-expander': 'onExpanderClick'
        },

        dashboardModelEvents: {
            'change': 'reload'
        },

        companySetEvents: {
            'change:name': 'onCompanySetNameChange',
            'companySetsChanged': 'onCompanySetsChanged',
            'companySetEditingComplete': 'reload'
        },

        onDestroy: function () {
            const me = this;

            if (me._connectedAccountHandler) {
                $(document).off('click', me._connectedAccountHandler);
                delete me._connectedAccountHandler;
            }
        },

        onCompanySetsChanged: function (changedCompanySetDescriptor) {
            const me = this;

            if (me.getProp('companySetDescriptor') === changedCompanySetDescriptor || me.getProp('primaryDimension') === 'companySetId') {
                RivalIQ.data.CacheLayer.invalidateAllCaches(false);
            }
        },

        onCompanySetNameChange: function (updatedCompanySet) {
            const me = this;

            if (me.getProp('primaryDimension') === 'companySetId') {
                me.render();
            }
        },

        _updateColumnSort: function (newMetrics) {
            const me = this;

            if (!newMetrics || !me.sortColumns) {
                return;
            }

            const orderBy = me.sortColumns[0];
            const currentMetric = me.metrics[orderBy];
            const newSelectedMetric = newMetrics[orderBy];

            // Change sort column if necessary
            if (!newSelectedMetric || !RivalIQ.metric.equal(newSelectedMetric, currentMetric)) {
                // Find where in the metrics array the previously selected
                // metric moved to
                const newOrder = _.findIndex(newMetrics, function (metric) {
                    return RivalIQ.metric.equal(metric, currentMetric);
                });

                if (newOrder !== -1) {
                    me.setProps({
                        orderBy: newOrder
                    }, {
                        silent: true
                    });
                }
            }
        },

        reload: function (newOptions) {
            const me = this;

            newOptions = newOptions || {};

            me._updateColumnSort(newOptions.metrics);
            _.extend(me, _.pick(me.dashboardModel, 'metrics', 'metric', 'metricsCollection'));

            // If we know the max returned results we can skip loading from the
            // server
            if (me.rowMax && me.getProp('limit') === -1 && !newOptions.metrics && me.rows.length < me.rowMax) {
                me.sortColumns = me.metricsCollection.getColumnSortOrder(me.getProp('orderBy'));
                return me.render();
            }

            RivalIQ.views.ItemView.prototype.reload.call(me);

            // re-render to force the table widths to be updated. See #20873
            me.requestRenderedFrame(function () {
                me.render();

                // A table widget that started as not expandable can become expandable with a
                // prop change (and vice versa), so we need to be able to update the class.
                me.$el.toggleClass('table-expandable', me.isTableExpandable());
            });
        },

        getFetchAttributes: function () {
            const me = this;

            const metrics = me.metricsCollection;
            me.sortColumns = me.metricsCollection.getColumnSortOrder(me.getProp('orderBy'));

            return {
                limit: Number(me.getProp('limit') || 0),
                sortOrder: Number(me.getProp('sortOrder') || 1),
                orderBy: metrics.getOrderBy(me.sortColumns)
            };
        },

        loadMetrics: function (attributes) {
            const me = this;
            const metrics = me.metricsCollection;
            let interval = me.getProp('interval');
            if (me.getProp('useAutoTimeInterval') || _.isUndefined(interval)) {
                me.setProps('useAutoTimeInterval', true);
                const timePeriod = me.getProp('timePeriod');
                // We don't want to get default interval for Twitter Discover. Twitter Discover returns timePeriod as undefined.
                if (timePeriod) {
                    interval = RivalIQ.metric.timePeriod.getDefaultInterval(timePeriod);
                }
            }

            let dimensionMetricDefinitions = [];
            const dimension = me.getProp('dimension');

            // NOTE if dimension is already present, do not add dimension metric rows
            if (dimension && !_.includes(metrics.first().get('dimensions'), dimension)) {
                dimensionMetricDefinitions = metrics.map(function (metric) {
                    return metric.build({
                        dimensions: metric.get('dimensions').concat(dimension)
                    });
                });
            }
            const dimensionMetrics = new RivalIQ.collections.MetricDefinitions(dimensionMetricDefinitions);
            let periodValueMetricDefinitions = metrics.reduce(function (memo, metric) {
                if (metric.get('useUniversalMetricsApi') && metric.get('showSparkline')) {
                    memo.push(metric.build({
                        _parent: metric.getIdentityForUniversalApi(),
                        timeAnalysisMode: RivalIQ.enums.timeAnalysisMode.MAIN_PERIOD,
                        dimensions: [interval]
                    }));
                }
                return memo;
            }, []);

            if (dimension && !_.includes(metrics.first().get('dimensions'), dimension)) {
                // If this table has dimension rows, we need to potentially grab sparkline data for the dimension rows too.
                const dimensionPeriodMetrics = _.reduce(dimensionMetricDefinitions, function (memo, dimensionMetric) {
                    if (dimensionMetric.get('useUniversalMetricsApi') && dimensionMetric.get('showSparkline')) {
                        memo.push(dimensionMetric.build({
                            _parent: dimensionMetric.getIdentityForUniversalApi(),
                            timeAnalysisMode: RivalIQ.enums.timeAnalysisMode.MAIN_PERIOD,
                            dimensions: [interval]
                        }));
                    }
                    return memo;
                }, []);
                periodValueMetricDefinitions = periodValueMetricDefinitions.concat(dimensionPeriodMetrics);
            }

            const periodValueMetrics = new RivalIQ.collections.MetricDefinitions(periodValueMetricDefinitions);

            return $.when(
                metrics.fetch(attributes),
                dimensionMetrics.fetch(attributes),
                periodValueMetrics.fetch(attributes)
            ).then(function (metrics, dimensions, periodValues) {
                return {
                    collection: metrics,
                    dimensions: dimensions,
                    periodValues: periodValues
                };
            });
        },

        loadData: function () {
            const me = this;
            const attributes = me.getFetchAttributes();

            // When we call loadData multiple times, we only want to take action on the last promise resolution.
            const loadId = me._loadDataId = _.uniqueId();

            const deferred = new $.Deferred();

            me.loadMetrics(attributes).done(function (properties) {
                if (loadId === me._loadDataId) {
                    _.extend(me, properties);
                    return deferred.resolve(properties);
                }
            }).fail(function (error) {
                if (loadId === me._loadDataId) {
                    return deferred.reject(error);
                }
            });

            return deferred;
        },

        /*
         * Defines min and max bounds for the table columns.
         */
        _setBounds: function (rows) {
            const me = this;

            me.bounds = _.reduce(rows, function (arr, row, rowIdx) {
                if (!row) {
                    return arr;
                }

                // columnBounds is an array with one entry for each column in the table.
                // Each entry is an array with the low and high bounds for that column.
                const columnBounds = _.map(row, function (data, colIdx) {
                    const bounds = arr[colIdx] || [];

                    // The bounds are used for rendering progress bars; SUMMARY_ROWs do not get rendered with
                    // progress bars so don't include them here.
                    if (data && data[0] !== SUMMARY_ROW) {
                        const value = RivalIQ.metric.data.rawValue(_.get(me.metrics, colIdx), data);

                        if (_.isUndefined(bounds[0]) || value < bounds[0]) {
                            bounds[0] = value;
                        }

                        if (_.isUndefined(bounds[1]) || value > bounds[1]) {
                            bounds[1] = value;
                        }
                    }

                    return bounds;
                });

                // If a row is expanded, we need to look at the dimension rows too since they might
                // change the bounds.
                if (me.isRowExpanded(rowIdx)) {
                    _.each(me.dimensionRows[rowIdx], function (dimensionRow) {
                        _.each(dimensionRow, function (data, dimensionRowColIdx) {
                            const bounds = columnBounds[dimensionRowColIdx] = columnBounds[dimensionRowColIdx] || [];

                            const value = RivalIQ.metric.data.rawValue(_.get(me.metrics, dimensionRowColIdx), data);
                            if (_.isUndefined(bounds[0]) || value < bounds[0]) {
                                bounds[0] = value;
                            }

                            if (_.isUndefined(bounds[1]) || value > bounds[1]) {
                                bounds[1] = value;
                            }
                        });
                    });
                }

                return columnBounds;
            }, []);
        },

        _limitColumns: function (columns) {
            const me = this;

            const columnLimit = me.getProp('columnLimit');
            if (_.isFinite(columnLimit)) {
                columns = columns.slice(0, columnLimit);
            }

            return columns;
        },

        _limitRowColumns: function (rows) {
            const me = this;

            const columnLimit = me.getProp('columnLimit');
            if (_.isFinite(columnLimit)) {
                rows = _.map(rows, function (row) {
                    if (_.isNull(row)) {
                        return row;
                    }

                    return row.slice(0, columnLimit);
                });
            }

            return rows;
        },

        _filterRows: function (rows, limitOverride) {
            const me = this;

            me.unfilteredRows = rows;
            let limit = (_.isNumber(limitOverride) ? limitOverride : Number(me.getProp('limit')));
            const filter = me.filter;
            let inclusionRules;

            function mustInclude(row) {
                return _.some(inclusionRules, function (rule, i) {
                    return rule(row[i]);
                });
            }

            const shouldFilterRows = (filter && me.getProp('filter')) || (_.exists(limit) && limit !== -1 && limit < rows.length);
            const filterPrivateOnly = me.dashboardModel.getCuration().isCustomDashboard;

            if (shouldFilterRows || filterPrivateOnly) {
                inclusionRules = _.compact(me.collection.map(function (metric) {
                    return _.bind(metric.mustInclude, metric);
                }));
            }

            if (filterPrivateOnly) {

                // If *all* of the metrics are for private sources, exclude any company that doesn't have any of the private data sources.
                const sources = _(me.getProp('metrics'))
                    .map(metric => RivalIQ.metric.property.source(metric))
                    .uniq()
                    .value();
                const allPrivate = _.every(sources, source => RivalIQ.enums.sourceDetails[source].privateData);
                if (allPrivate) {
                    rows = _.filter(rows, row => {
                        if (mustInclude(row)) {
                            return true;
                        }

                        return !(_.every(_.tail(row), _.isUndefined));
                    });
                }
            }

            if (shouldFilterRows) {
                limit = (!_.exists(limit) || limit === -1 ? Infinity : limit);

                /*
                 * Filter rows so that we don't have more than the limit, but
                 * retain any rows that must be included in the output
                 * (Typically this is a row showing focus company data)
                 */
                const filteredRows = [];
                let count = 0;
                _.each(rows, function (row) {
                    if (filter && filter(row) && !mustInclude(row)) {
                        return filteredRows.push(null);
                    }

                    if (count >= limit) {

                        // If we are adding a new row but are over the limit
                        // remove the last previous inclusion that was not
                        // a mustInclude row to keep us under the limit.
                        if (mustInclude(row)) {
                            const replace = _.findLastIndex(filteredRows, function (previousRow) {
                                return previousRow && !mustInclude(previousRow);
                            });
                            filteredRows[replace] = null;

                            count++;
                            return filteredRows.push(row);
                        }

                        return filteredRows.push(null);
                    }

                    count++;
                    return filteredRows.push(row);
                });

                return filteredRows;
            }

            return rows;
        },

        _getRawColumnData: function () {
            const me = this;

            let rowIdOrder;
            const columns = me.collection.map(function (metric, idx) {
                let value = metric.getValue({
                    format: false,
                    filterCompaniesWithoutPrivatePresences: false
                });

                // We are rendering a table with all fixed dimensions
                // Fake the correct data format so we get a single row
                if (!_.isArray(value)) {
                    return [[metric.get(me.getProp('dimension')), value]];
                }

                // make sure that the order of ids for values matches all the other rows; it's not a good idea
                // to assume metric.getValue() returns rows in the same order for every metric in the collection,
                // that is not guaranteed by metric.getValue().
                if (idx === 0) {
                    rowIdOrder = _.map(value, _.head);
                } else {
                    const valuesById = _.keyBy(value, _.head);
                    value = _.map(rowIdOrder, function (rowId) {
                        return valuesById[rowId];
                    });
                }

                // If we are inverting the table add the column headers to the
                // first column
                if (me.invert) {
                    const name = metric.getName({
                        dimensions: me.getProp('nameDimensions')
                    });
                    return [[name]].concat(value);
                }

                return value;
            });

            return columns;
        },

        _getRows: function (sortColumns, sortOrder, sortFunctions) {
            const me = this;

            const columns = me._getRawColumnData();

            let rows = columns;

            if (!me.invert) {
                rows = _.zip.apply(null, columns);
                rows = rows.sort(sortRow(sortColumns, sortOrder, sortFunctions));
            }

            return me._filterRows(rows);
        },

        _getDimensionRows: function (rows, sortColumns, sortOrder, sortFunctions) {
            const me = this;

            const dimensions = me.dimensions;
            if (_.isEmpty(me.dimensions)) {
                return;
            }

            // dimensionValues is an array of num columns length.
            // each array has num rows length.
            // each column might not have the same number of rows.
            const dimensionValues = dimensions.invoke('getValue', {
                format: false
            });

            const rowsAcc = _.reduce(dimensionValues, (rowsAcc, columnDimensionValues, columnIdx) => {
                _.each(columnDimensionValues, (result) => {
                    const rowKey = _.take(result, result.length - 1).join(';');
                    rowsAcc[rowKey] ??= [];
                    rowsAcc[rowKey][columnIdx] = result;
                });
                return rowsAcc;
            }, {});

            // dimensionRows is an array that is num rows long.
            // each row is an array with num columns values.
            // each array in the row should have the same fixed dims.
            const dimensionRows = _.values(rowsAcc);
            dimensionRows.sort(sortRow(sortColumns, sortOrder, sortFunctions));

            return _.reduce(rows, function (memo, row) {
                // NOTE when a limit is applied, rows after the limit are null,
                // so don't explode
                if (row) {
                    const dimension = row[0][0];
                    memo.push(_.filter(dimensionRows, function (dimensionRow) {
                        return (_.get(dimensionRow, [0, 0]) === dimension);
                    }));
                } else {
                    // push null to make sure that dimensionRows is parallel to rows.
                    memo.push(null);
                }

                return memo;
            }, []);
        },

        _showPrivateNoPresenceView: function () {
            const me = this;

            // handle no data because of missing permissions.
            const widgetInstanceState = me.dashboardModel.getWidgetInstanceState();
            const metrics = _.get(me.dashboardModel, ['metrics']);
            if (widgetInstanceState && metrics && _.isEqual(RivalIQ.metric.getFreeDimensions(metrics[0]), ['companyId'])) {
                const sources = _(metrics)
                    .reject({
                        property: 'name'
                    })
                    .map(metric => {
                        return RivalIQ.metric.property.source(metric);
                    })
                    .uniq()
                    .value();

                if (sources.length === 1 && RivalIQ.enums.sourceDetails[sources[0]].privateData && widgetInstanceState.sources[sources[0]] && _.isEmpty(widgetInstanceState.companies)) {
                    const View = RivalIQ.apps.Insights.views.getNoPresenceView(sources[0]);
                    const view = new View({
                        dashboardModel: me.dashboardModel
                    });
                    const html = view.render().el;

                    me.attachElContent(html);
                    return true;
                }
            }
        },

        _showCompanySetUpsellView: function () {
            const me = this;

            if (isDisallowedCompanySetAggregation(me)) {
                const html = Marionette.TemplateCache.get('#metricTableCompanySetsNotAllowedTemplate')();
                me.attachElContent(html);
                return true;
            }
        },

        _renderTemplate: function () {
            const me = this;

            const template = me.getTemplate();
            if (template === false) {
                return;
            }

            if (!template) {
                throw new Marionette.Error({
                    name: 'UndefinedTemplateError',
                    message: 'Cannot render the template since it is null or undefined.'
                });
            }

            if (me._showPrivateNoPresenceView() || me._showCompanySetUpsellView()) {
                return;
            }

            me._generateRowData();

            let html;
            if (me.rows.length) {
                me._renderAndAttachHtml(template);
            } else {
                const EmptyView = me.getEmptyView();
                let emptyViewOptions = me.getOption('emptyViewOptions');
                if (_.isFunction(emptyViewOptions)) {
                    emptyViewOptions = emptyViewOptions.call(me);
                }
                const view = new EmptyView(emptyViewOptions);
                html = view.render().el;

                me.attachElContent(html);
            }

            return me;
        },

        _renderAndAttachHtml: function (template) {
            const me = this;

            let data = me.serializeData(me.rows, me.dimensionRows);
            data = me.mixinTemplateHelpers(data);
            const html = Marionette.Renderer.render(template, data, me);

            me.attachElContent(html);

            me._asyncRenderRows(data);
        },

        _asyncRenderRows: function (data) {
            const me = this;

            const SUBSEQUENT_CHUNK_DELAY_MS = 100;
            const NUM_ROWS_PER_CHUNK = (me.getProp('rowsPerChunk') || 8);

            const rowGroups = _.chunk(data.rows, NUM_ROWS_PER_CHUNK);

            let currentChunkIdx = 0;
            const isExporting = me.dashboardModel.isExporting();

            // Since we're rendering asyncronously, it's possible that we're inflight rendering
            // rows when a prop changes, which results in another render pass happening at the
            // same time. Only render calls with the latest renderId should actually do any work.
            const renderId = me._renderId = (me._renderId || 0) + 1;

            function renderChunk() {
                if (renderId !== me._renderId) {
                    return;
                }

                const rowGroup = rowGroups[currentChunkIdx];

                let startId;
                const addlRowsHtml = _.map(rowGroup, function (cells, rowIdxInGroup) {
                    const rowIdx = rowIdxInGroup + currentChunkIdx * NUM_ROWS_PER_CHUNK;
                    if (_.isUndefined(startId)) {
                        startId = rowIdx;
                    }
                    return me.renderMetricRow(cells, rowIdx);
                }).join('');

                me.appendRowToBody(addlRowsHtml, startId, rowGroup.length);

                currentChunkIdx++;
                if (currentChunkIdx >= rowGroups.length) {
                    me.onAllRowsRendered();

                    if (isExporting) {
                        me.stopAsyncRender();
                    }
                } else {

                    if (currentChunkIdx === 1 && !isExporting) {
                        setTimeout(function () {
                            me.requestRenderedFrame(function () {
                                renderChunk();
                            });
                        }, SUBSEQUENT_CHUNK_DELAY_MS);
                    } else {

                        me.requestRenderedFrame(function () {
                            renderChunk();
                        });
                    }
                }
            }

            // start an async render on export so we know to wait until all the rows are on the page.
            if (isExporting) {
                me.startAsyncRender();
            }
            me.requestRenderedFrame(function () {
                me.ui.tbody.empty();
                renderChunk();
            });
        },

        appendRowToBody: function (newRowHtml) {
            const me = this;

            me.ui.tbody.append(newRowHtml);
        },

        onAllRowsRendered: function () {
            const me = this;

            me.buildDescriptionPopovers();
            me.$('[data-toggle="tooltip"]').tooltip();

            if (!_.isEmpty(me.dimensionRows)) {
                // NOTE because the data and renderers are separated, and
                // because it is currently impossible in CSS to style siblings,
                // we must use jQuery. see #15907
                me.$('tr.master-row').each(function (i, tr) {

                    let idx = $(tr).data('index'); // can't just use `i` as it's possible that we've omitted some rows.
                    if (_.isUndefined(idx)) {
                        idx = i;
                    }

                    const dimensionRow = me.dimensionRows[idx];
                    if (_.isEmpty(dimensionRow)) {
                        $(tr).find('.table-expander').remove();
                    }
                });
            }

            // The way we render tables is complicated and in this case, we need to listen to a click event on a tooltip in a cell. Neither
            // the cell nor the tooltip will be rendered in a way that we can attach an object or click handler to it, so we need to listen
            // listen to live events. Filter on cid in case there are multiple tables rendered on the page.
            if (!me._connectedAccountHandler) {
                me._connectedAccountHandler = $(document).on('click', '.x-view-connected-accounts', function (event) {
                    const $el = $(event.target);
                    if ($el.data('cid') !== me.cid) {
                        return;
                    }
                    me.onViewConnectedAccountsClick($el);
                });
            }
        },

        onViewConnectedAccountsClick: function ($el) {
            const me = this;
            let source = $el.data('source');
            if (source === RivalIQ.enums.sources.FACEBOOK_INSIGHTS) {
                source = RivalIQ.enums.sources.FACEBOOK;
            }
            const sources = RivalIQ.enums.sources;
            if (_.includes([sources.INSTAGRAM_INSIGHTS, sources.FACEBOOK], source)) {
                RivalIQ.execute('show:modal', new RivalIQ.apps.AccountSettings.views.ConnectedPagesModal({
                    model: new Backbone.Model({
                        source: source
                    })
                }));
            } else if (source === sources.TWITTER_INSIGHTS) {
                const model = RivalIQ.data.channels.get(source);
                RivalIQ.execute('show:modal', new RivalIQ.apps.AccountSettings.views.MultiTokenConnectionModal({
                    model: model
                }));
            } else if (source === sources.LINKEDIN) {
                const modalData = {
                    noNavigate: true
                };
                if (me.dashboardModel.getWidgetInstanceState) {
                    const instanceData = me.dashboardModel.getWidgetInstanceState(me.dashboardModel.getProp('focusWidgetInstanceId'));
                    modalData.linkedInPresences = instanceData.linkedInPresences;
                    modalData.unassociatedPresences = instanceData.unassociatedPresences;
                    modalData.accountCompanies = instanceData.accountCompanies;
                } else {
                    const curation = me.dashboardModel.getCuration();
                    modalData.accountCompanies = curation.accountCompanies;
                    modalData.accountPresences = curation.accountPresences;
                }
                RivalIQ.execute('show:modal', new RivalIQ.apps.Insights.views.LinkedInPresenceConfigurationModal(modalData));
            }
        },

        _getPrivateDataNotConnectedTooltip: function (metric, companyId) {
            const me = this;

            if (me.dashboardModel.getWidgetInstanceState && companyId) {
                const widgetInstanceState = me.dashboardModel.getWidgetInstanceState();
                const companies = _.get(widgetInstanceState, ['companies']);
                const hasPrivateData = _.find(companies, {
                    id: companyId
                });
                const source = metric.getSource();
                if (!_.isUndefined(source) && RivalIQ.enums.sourceDetails[source].privateData && _.get(widgetInstanceState, ['sources', source]) && !hasPrivateData) {
                    const tooltipTemplate = Marionette.TemplateCache.get('#tableNoPrivateDataTooltip');
                    const company = RivalIQ.state.landscape.getCompanyById(companyId);
                    if (company) {
                        return tooltipTemplate({
                            companyName: company.get('name'),
                            source: source,
                            cid: me.cid
                        });
                    }
                }
            }
        },

        _renderCellTemplate: function (metric, data, className, view, columnIndex, rowIndex) {
            const me = this;

            const showSourceIcons = me.dashboardModel.getCuration().isCustomDashboard;

            const selectedBenchmarkCompareAgainst = me.getProp('selectedBenchmarkCompareAgainst');
            const tooltipTemplate = Marionette.TemplateCache.get('#tableCellTooltip');
            if (RivalIQ.metric.property.isNumeric(metric.attributes)) {
                const tipValue = (_.isNumber(_.last(data)) ? metric.formatExpanded(data) : '--');
                const channel = metric.get('channel');
                const property = metric.get('property');
                let tooltip = tooltipTemplate({
                    name: metric.getName(),
                    value: tipValue,
                    channel: channel,
                    property: property,
                    sourceIcon: (showSourceIcons ? RivalIQ.enums.sourceDetails[metric.getSource()].iconSvg : null)
                });

                const elideMetric = false;
                let partialDataPopover = null;

                const {primaryDimension, primaryDimensionId, calculationMetadata} = metric.getPrimaryDimensionAndCalculationMetadata(data);

                if (!_.isEmpty(_.get(calculationMetadata, ['missingSources']))) {
                    partialDataPopover = RivalIQ.utils.incompleteData.getIncompleteDataPopover(primaryDimension, primaryDimensionId, metric, calculationMetadata);
                } else if (primaryDimension === 'companySetId' && primaryDimensionId) {

                    const aggregateName = metric.get('companyIdAggregate').replace('companies', '').toLowerCase();
                    const partialDataTooltip = RivalIQ.utils.incompleteData.getBenchmarkIncompleteDataDetails(calculationMetadata, metric, {
                        name: RivalIQ.state.utils.getCompanySetName(primaryDimensionId),
                        aggregateName: aggregateName
                    });

                    if (partialDataTooltip) {
                        partialDataPopover = {
                            popover: partialDataTooltip.partialDataTooltip || partialDataTooltip.dataNotAvailableTooltip
                        };
                    }
                } else if ((selectedBenchmarkCompareAgainst && _.head(data) === selectedBenchmarkCompareAgainst)) {
                    const benchmarkCompareAgainst = RivalIQ.apps.Overview.utils.getSelectedBenchmarkCompareAgainst(selectedBenchmarkCompareAgainst);
                    const partialDataTooltip = RivalIQ.utils.incompleteData.getBenchmarkIncompleteDataDetails(calculationMetadata, metric, benchmarkCompareAgainst);

                    if (partialDataTooltip) {
                        partialDataPopover = {
                            popover: partialDataTooltip.partialDataTooltip || partialDataTooltip.dataNotAvailableTooltip
                        };
                    }
                }

                if (_.isNumber(_.last(data)) &&
                    selectedBenchmarkCompareAgainst &&
                    _.head(data) === selectedBenchmarkCompareAgainst &&
                    metric.get('timeAnalysisMode') === RivalIQ.enums.timeAnalysisMode.DELTA_RELATIVE) {

                    tooltip = Marionette.TemplateCache.get('#benchmarkDeltaRelTableCellTooltip')({
                        value: tipValue
                    });
                }

                if (_.isNull(_.last(data)) && partialDataPopover) {
                    tooltip = partialDataPopover?.popover?.content;
                    partialDataPopover = null;
                }

                const cellTemplateWithTip = Marionette.TemplateCache.get('#metricTableCellWithTipTemplate');
                let summaryType = null;
                if (data && data[0] === SUMMARY_ROW) {
                    if (hasPrimaryDimensionSummaryRowAggregate(me)) {
                        summaryType = _.capitalize(getPrimaryDimensionSummaryAggregate(me, columnIndex));
                    } else {
                        const customSummary = _.get(me.getProp('customSummaryMetrics'), [columnIndex - 1]);
                        if (customSummary) {
                            summaryType = RivalIQ.apps.Overview.utils.getSelectedBenchmarkCompareAgainst(customSummary).customSummaryName;
                        }
                    }
                    className += ' custom-summary-cell';
                }

                const privateDataNotConnectedTooltip = me._getPrivateDataNotConnectedTooltip(metric, _.get(me.rows[rowIndex], [0, 0]));
                if (privateDataNotConnectedTooltip) {
                    tooltip = privateDataNotConnectedTooltip;
                }

                if (!partialDataPopover?.popover?.content) {
                    partialDataPopover = null;
                }

                return cellTemplateWithTip({
                    className: className,
                    value: (elideMetric ? '' : metric.format(data)),
                    tooltip: tooltip,
                    partialDataPopover: partialDataPopover || null,
                    summaryType: summaryType
                });
            } else if (RivalIQ.metric.property.type(metric.attributes) === RivalIQ.enums.dataType.STRING && _.isEqual(metric.getFreeDimensions(), ['companySetId'])) {
                const cellTemplateWithTip = Marionette.TemplateCache.get('#metricTableCellWithTipTemplate');
                const metricName = RivalIQ.metric.names.metricName(metric.attributes);
                const tooltip = `${metricName} is available for individual companies. Expand the row to see ${metricName} for each company.`;
                return cellTemplateWithTip({
                    className: className,
                    value: metric.format(data),
                    tooltip: tooltip,
                    partialDataPopover: null
                });
            }

            const fn = getCellTemplate(view, columnIndex);
            return fn({
                className: className,
                value: metric.format(data)
            });
        },

        onAttachLoaded: function () {
            const me = this;

            if (me.rows && me.rows.length) {
                const data = me.serializeData(me.rows, me.dimensionRows);
                me.renderTableHeaders(me.ui.headerRow[0], data.columns);
                me.checkEllipsibleContentForOverflow();
                widgets.table.HeaderCell.initializePopovers(me.el);
            }
        },

        renderTableHeaderRow: function () {
            const me = this;

            const headerCell = new widgets.table.HeaderCell({
                invert: me.invert,
                collection: me.collection,
                sortable: me.getProp('sortable'),
                sortedBy: me.sortColumns[0],
                sortOrder: Number(me.getProp('sortOrder'))
            });

            const columns = me.getColumns(me.rows);

            return _.map(columns, function (column, i) {
                return headerCell.render(column, i);
            }).join('');
        },

        renderTableHeaders: function (headerRowNode, columns) {
            const me = this;

            _.each(headerRowNode?.children, function (node, i) {
                if (columns[i]) {
                    widgets.table.HeaderCell.renderText(node, columns[i], me.getProp('dontAbbreviateHeaders'));
                }
            });
        },

        buildDescriptionPopovers: function () {
            const me = this;
            const isInsideModal = me.dashboardModel.getProp('modalWidget');
            if (isInsideModal) {
                me.$el.find('[data-toggle=popover]:last-child').popover({
                    html: true,
                    placement: 'right'
                });
            }

            me.$el.find('[data-toggle=popover]:not(th)').popover({
                html: true,
                placement: 'auto'
            });

            me.$el.find('th[data-toggle=popover]').each((idx, el) => {
                RivalIQ.utils.tooltip.addPopover($(el), {
                    html: true,
                    trigger: 'hover',
                    container: 'body'
                }, {
                    placement: 'top auto',
                    delay: {
                        show: 700
                    }
                });
            });

            me.$el.find('[data-toggle=popoverCell]').each((idx, el) => {
                RivalIQ.utils.tooltip.addPopover($(el), {
                    html: true,
                    trigger: 'hover',
                    container: 'body',
                    placement: 'auto'
                }, {
                    delay: {
                        show: 700
                    }
                });
            });
        },

        // Build up the multi-dimensional array of table data from metric data
        _generateRowData: function () {
            const me = this;
            const sortOrder = me.getProp('sortOrder');
            const sortFunctions = me.getSortFunctions();

            me.rows = me._getRows(me.sortColumns, sortOrder, sortFunctions);
            me.dimensionRows = me._getDimensionRows(me.rows, me.sortColumns, sortOrder, sortFunctions);
        },

        // Returns an array of functions to determine sort order
        getSortFunctions: function () {
            const me = this;
            if (me.metricsCollection) {
                return me.metricsCollection.map(function (metric) {
                    return metric.getSortFunction();
                });
            }
        },

        /*
         * Return inverted column headers
         * Column headers for inverted tables are the dimension labels for the
         * row
         */
        getInvertedColumnHeaders: function (rows) {
            const me = this;

            const metric = me.collection.first();
            const freeDimension = _.last(RivalIQ.metric.getFreeDimensions(metric.attributes));
            const data = _.flatten(_.map(rows, function (row) { return _.tail(row); }));
            const values = RivalIQ.metric.dimensionValues(metric.attributes, freeDimension, data);
            const includeDesc = me.getProp('showDescriptions');
            const useCustomAbbrs = me.getProp('customAbbreviations');

            return _.reduce(values, function (arr, dimensionValue) {
                arr.push({
                    name: metric.byDimension('name', dimensionValue, freeDimension),
                    abbreviatedName: (useCustomAbbrs ? metric.byDimension('format', dimensionValue, freeDimension) : undefined),
                    description: (includeDesc ? metric.byDimension('description', dimensionValue, freeDimension) : undefined)
                });

                return arr;
            }, [{
                name: 'Metric'
            }]);
        },

        shouldShowHeaderSourceIcons: function () {
            const me = this;
            return me.dashboardModel.getCuration().isCustomDashboard;
        },

        /*
         * Return standard column headers
         * Standard column headers are the name of the metric
         */
        getStandardColumnHeaders: function () {
            const me = this;
            const includeDesc = me.getProp('showDescriptions');
            const omitTimeAnalysisModeDescription = me.getProp('omitTimeAnalysisModeDescription');
            const options = {
                dimensions: me.getProp('nameDimensions'),
                name: false,
                omitTimeAnalysisModeDescription: omitTimeAnalysisModeDescription
            };

            const showSourceIcons = me.shouldShowHeaderSourceIcons();

            return me.collection.map(function (metric, idx) {
                const metricOptions = _.defaults({
                    descriptionDimensions: me.getDescriptionDimensions(metric)
                }, options);

                let name = metric.getName(metricOptions);
                const source = metric.get('source');

                let aggregateAbbrev;
                if (idx !== 0 && _.isEqual(metric.getFreeDimensions(), ['companySetId'])) {
                    let aggregate;
                    switch (metric.get('companyIdAggregate')) {
                        case 'companiesAverage':
                            aggregate = 'Average';
                            aggregateAbbrev = '(Avg.)';
                            break;
                        case 'companiesMedian':
                            aggregate = 'Median';
                            aggregateAbbrev = '(Med.)';
                            break;
                        case 'companiesSum':
                            aggregate = 'Sum';
                            aggregateAbbrev = '(Sum)';
                            break;

                        default:
                            aggregateAbbrev = null;
                    }

                    if (aggregateAbbrev) {
                        name = `${name} (${aggregate})`;
                    }
                }

                let channelSvg = null;
                let abbreviatedName;
                if (idx !== 0 && showSourceIcons) {
                    channelSvg = RivalIQ.data.channels.get(source).get('iconSvg');
                    abbreviatedName = metric.getName(_.defaults({
                        dimensions: _.without(metricOptions.dimensions, 'channel')
                    }));
                }

                let description = undefined;
                if (includeDesc) {
                    description = RivalIQ.metric.render.description(metric.attributes, metricOptions);

                    if (!description && idx === 0 && hasPrimaryDimensionSummaryRowAggregate(me)) {
                        const primaryDimension = me.getProp('primaryDimension');
                        description = _.escape(RivalIQ.metric.search.getDimensionValue(metric.attributes, 'description', primaryDimension));
                    }
                }

                return {
                    name: name,
                    abbreviatedName: abbreviatedName,
                    property: metric.get('property'),
                    description: description,
                    channelSvg: channelSvg,
                    aggregate: aggregateAbbrev || null
                };
            });
        },

        getDescriptionDimensions: function (metric) {
            const descriptionDimensions = metric.get('descriptionDimensions');
            // NOTE if we don't have 'descriptions' bail out now
            if (!descriptionDimensions) {
                return;
            }

            const dimensions = metric.get('dimensions');
            const freeDimensions = metric.getFreeDimensions();
            return _.union(descriptionDimensions, _.difference(dimensions, freeDimensions));
        },

        getColumns: function (rows) {
            const me = this;

            let columns;
            if (me.invert) {
                columns = me.getInvertedColumnHeaders(rows);
            } else {
                columns = me.getStandardColumnHeaders();
            }

            // if we have a column limit, apply it to headers and all rows
            return me._limitColumns(columns);
        },

        serializeData: function (rows, dimensionRows) {
            const me = this;

            const sortOrder = me.getProp('sortOrder');

            me._setBounds(rows);

            const columns = me.getColumns(rows);
            rows = me._limitRowColumns(rows);

            return {
                tableClassName: me.getTableClassName(),
                columns: columns,
                sortOrder: sortOrder,
                rows: rows,
                dimensionRows: dimensionRows
            };
        },

        getTableClassName: function () {
            const me = this;

            if (!_.isUndefined(me.dashboardModel.tableClassName)) {
                return _.result(me.dashboardModel, 'tableClassName');
            }

            const hasTextTypeMetric = me.metricsCollection.some(function (m) {
                return (m.getMetricType() === RivalIQ.enums.metricType.TEXT);
            });

            // NOTE if the table contains ANY metrics with a type of `TEXT`
            // then we don't want to use fixed cell heights with the
            // `table-fix` class in details mode
            //
            // See rivaliq-server/#13607 for more information
            // https://github.com/RivalIQ/rivaliq-server/issues/13607#issuecomment-315668587
            if (me.dashboardModel.isFocus() && hasTextTypeMetric) {
                return '';
            }

            return 'table-fix';
        },

        getTrailingCellView: function (columnIndex) {
            const me = this;

            let numColumns = me.metricsCollection.length;
            const columnLimit = me.getProp('columnLimit');
            if (_.isFinite(columnLimit) && columnLimit > 0) {
                numColumns = Math.min(numColumns, columnLimit);
            }

            // NOTE this intentionally suppresses the metric defined cell views
            if (columnIndex < numColumns - 1) {
                return;
            }

            if (!RivalIQ.metric.property.isNumeric(me.metrics[columnIndex])) {
                return;
            }

            // Don't show bars for delta metrics. See #33650
            const timeAnalysisMode = me.metrics[columnIndex].timeAnalysisMode;
            if (_.includes([RivalIQ.enums.timeAnalysisMode.DELTA_ABSOLUTE, RivalIQ.enums.timeAnalysisMode.DELTA_RELATIVE], timeAnalysisMode)) {
                return;
            }

            return RivalIQ.views.metric.Bar;
        },

        getMetricModel: function (rowIndex, columnIndex) {
            const me = this;
            return me.collection.at(me.invert ? rowIndex : columnIndex);
        },

        // TODO one model should be enough..
        getDefinitionModel: function (rowIndex, columnIndex, isSubCell) {
            const me = this;
            let coll = me.metricsCollection;
            const idx = (me.invert ? rowIndex : columnIndex);
            // NOTE we should only use the dimension model if it corresponds to
            // the "special" `name` property
            if (isSubCell && me.dimensions.at(idx).get('property') === 'name') {
                coll = me.dimensions;
            }
            return coll.at(idx);
        },

        getCellView: function (data, rowIndex, columnIndex, dimensionView, isSubCell) {
            const me = this;

            const def = me.getDefinitionModel(rowIndex, columnIndex, isSubCell);
            if (def) {
                if (dimensionView && def.get('dimensionView')) {
                    return def.get('dimensionView');
                }

                if (columnIndex !== 0) {
                    if (def.get('showSparkline')) {
                        return RivalIQ.views.metric.SparklineTableCell;
                    }

                    // Show a bar for the last metric if trailingBar is set and the last metric is numeric.
                    if (me.getProp('trailingBar') && RivalIQ.metric.property.isNumeric(me.metrics[columnIndex]) && data && data[0] !== SUMMARY_ROW) {
                        return me.getTrailingCellView(columnIndex);
                    }
                }

                if (me.displayMetricCellViews) {
                    return def.getCellView(data);
                }
            }
        },

        getDimensionViewMoreData: function (masterRowIndex, rowValues, dimensionRows) {
            return undefined;
        },

        isRowExpanded: function (i) {
            const me = this;
            const rowExpansions = getExpansions(me);
            return _.includes(rowExpansions, i);
        },

        getCellClassName: function (metric, data, columnIndex) {
            const me = this;
            let className = '';

            if (me.getProp('highlightSortColumn') && me.sortColumns[0] === columnIndex) {
                className = 'sorted ';
            }

            if (metric.getTimeAnalysisMode() === RivalIQ.enums.timeAnalysisMode.DELTA_RELATIVE) {
                className += `${RivalIQ.formatter.relativeMetricClass(_.last(data))} `;
            }

            return className;
        },

        renderCellView: function (View, metric, data, rowIndex, columnIndex) {
            const me = this;
            if (_.isFunction(View)) {
                const view = new View({
                    model: metric,
                    data: data,
                    index: rowIndex,
                    columnIndex: columnIndex,
                    rows: me.rows,
                    bounds: me.bounds[columnIndex],
                    table: me,
                    props: me.getProps()
                });

                const selectedBenchmarkCompareAgainst = me.getProp('selectedBenchmarkCompareAgainst');
                if (selectedBenchmarkCompareAgainst && data?.[0] === selectedBenchmarkCompareAgainst) {
                    view.color = RivalIQ.enums.colors.competitor;
                }

                return view.render().el.outerHTML;
            }

            let content;
            let className = me.getCellClassName(metric, data, columnIndex);
            if (_.isFunction(View.render)) {
                const viewClassName = (_.exists(View.className) ? View.className : '');
                className = [className, viewClassName].join('');
                content = View.render(metric, data, me);
            }

            const fn = getCellTemplate(me, columnIndex);
            return fn({
                className: className,
                value: content
            });
        },

        renderDimensionRows: function (rows, dimensionRows, i, isExpanded) {
            const me = this;
            const rowValues = rows[i];

            isExpanded = isExpanded || me.isRowExpanded(i);
            const rowsHtml = _.map(dimensionRows[i], function (dimensionRow, j) {
                return $.tagHtml('tr', {
                    class: _.compact([
                        'subordinate-row',
                        (isExpanded ? 'expanded' : '')
                    ]),
                    'data-index': j,
                    html: _.map(dimensionRow, function (data, k) {
                        return me.renderSubCell(data, k, i);
                    }).join(''),
                    'data-master-index': i
                });
            }).join('');

            const viewMoreData = me.getDimensionViewMoreData(i, rowValues, dimensionRows[i]);
            let viewMore;
            if (viewMoreData) {
                viewMore = $.tagHtml('tr', {
                    class: _.compact([
                        'x-export-hide subordinate-row subordinate-row-view-more',
                        (isExpanded ? 'expanded' : '')
                    ]),
                    html: $.tagHtml('td', {
                        colspan: dimensionRows[i][0].length,
                        html: viewMoreData.message
                    }),
                    'data-master-index': i
                });
            } else {
                viewMore = '';
            }

            return rowsHtml + viewMore;
        },

        getDefaultClassName: function (metric, columnIndex) {
            if (columnIndex === 0) {
                return '';
            }

            const metricType = metric.getMetricType();
            if (metricType === RivalIQ.enums.metricType.TEXT) {
                return 'text-metric-table-cell';
            }

            if (metricType === RivalIQ.enums.metricType.STRING) {
                return 'string-metric-table-cell';
            }

            return 'metric-table-cell';
        },

        renderSubCell: function (data, columnIndex, masterRowIndex) {
            const me = this;

            const CellView = me.getCellView(data, masterRowIndex, columnIndex, true, true);

            let metric;
            const selectedBenchmarkCompareAgainst = me.getProp('selectedBenchmarkCompareAgainst');
            if (selectedBenchmarkCompareAgainst && data?.[0] === selectedBenchmarkCompareAgainst) {
                metric = me.benchmarkMetrics[columnIndex];
            } else {
                metric = me.dimensions.at(columnIndex);
            }

            if (CellView) {
                return me.renderCellView(CellView, metric, data, masterRowIndex, columnIndex);
            }
            const className = me.getCellClassName(metric, data, columnIndex) +
                (metric.getClassName(data) || me.getDefaultClassName(metric, columnIndex));

            return me._renderCellTemplate(metric, data, className);
        },

        renderCell: function (data, columnIndex, rowIndex) {
            const me = this;

            let metric;
            const selectedBenchmarkCompareAgainst = me.getProp('selectedBenchmarkCompareAgainst');
            if (selectedBenchmarkCompareAgainst && data?.[0] === selectedBenchmarkCompareAgainst) {
                metric = me.benchmarkMetrics[columnIndex];
            } else {
                metric = me.getMetricModel(rowIndex, columnIndex);
            }

            let CellView;
            if (metric.get('cellView') !== false) {
                CellView = me.getCellView(data, rowIndex, columnIndex);
            }

            if (CellView) {
                return me.renderCellView(CellView, metric, data, rowIndex, columnIndex);
            }

            // Special case for rendering the metric name column of an
            // inverted table
            if (me.invert && columnIndex === 0) {
                return cellTemplate({
                    className: 'invert-table-cell',
                    value: _.last(data)
                });
            }

            const className = me.getCellClassName(metric, data, columnIndex) +
                (metric.getClassName(data) || me.getDefaultClassName(metric, columnIndex));

            return me._renderCellTemplate(metric, data, className, me, columnIndex, rowIndex);
        },

        rowClass: function (cells, i) {
            const me = this;
            const rowClass = [_.result(me, 'rowClassName')];

            if (i > 0 && !me.rows[i - 1]) {
                rowClass.push('ellipsed');
            }

            const rowExpansions = getExpansions(me);
            if (_.includes(rowExpansions, i)) {
                rowClass.push('expanded');
            }

            if (me.dimensionRows && _.isEmpty(me.dimensionRows[i])) {
                rowClass.push('unexpandable');
            }

            return rowClass.join(' ');
        },

        renderMetricRowCells: function (cells, i) {
            const me = this;

            return _.map(cells, function (cellData, j) {
                return me.renderCell(cellData, j, i);
            }).join('');
        },

        renderMetricRow: function (cells, i) {
            const me = this;

            if (!cells) {
                return '';
            }

            const cellHtml = me.renderMetricRowCells(cells, i);
            let html = $.tagHtml('tr', {
                class: _.compact([
                    (me.dimensions.length ? 'master-row' : 'x-data-row'),
                    me.rowClass(cells, i)
                ]),
                'data-index': i,
                html: cellHtml
            });

            if (me.dimensionRows && me.dimensionRows[i] && me.isRowExpanded(i)) {
                html += me.renderDimensionRows(me.rows, me.dimensionRows, i);
            }

            return html;
        },

        templateHelpers: function () {
            const me = this;

            return {
                renderTableHeaderRow: function () {
                    return me.renderTableHeaderRow();
                }
            };
        },

        showLoadingRow: function () {
            const me = this;

            me.hideLoadingRow();

            const loadingRow = $.tagHtml('tr', {
                class: 'loading-row',
                html: $.tagHtml('td', {
                    colspan: me.ui.headerRow.children().length,
                    html: '<span class="fa fa-circle-o-notch fa-spin fa-fw" />'
                })
            });
            me.ui.tbody.append(loadingRow);
        },

        hideLoadingRow: function () {
            const me = this;

            if (me.ui.tbody?.find) {
                me.ui.tbody.find('.loading-row').remove();
            }
        },

        _onRowClick: function (e) {
            const me = this;

            let dimensionRow;
            let row;
            const target = $(e.currentTarget);
            const dataIndex = target.data('index');

            if (target.hasClass('subordinate-row') || target.parents('.subordinate-row').length > 0) {
                const masterIdx = target.data('master-index');
                dimensionRow = me.dimensionRows[masterIdx][dataIndex];
                row = me.rows[masterIdx];
            } else {
                row = me.rows[dataIndex];
            }

            if (row) {
                me.triggerMethod('row:click', row, e, dimensionRow);
            }
        },

        _setSortProps: function (props) {
            const me = this;
            me.setProps(props);
        },

        onSortClick: function (e) {
            const me = this;
            const target = $(e.currentTarget);

            target.popover('hide');

            const index = target.data('orderBy');
            let sortOrder = Number(target.data('sortOrder'));
            sortOrder = (sortOrder === 1 ? -1 : 1);

            me._setSortProps({
                orderBy: Number(index),
                rowExpansions: undefined,
                sortOrder: Number(sortOrder)
            });

            RivalIQ.execute('tracking:push:auth', RivalIQ.tracking.metric.SortedTable, {
                widgetId: me.dashboardModel.widgetId,
                orderBy: me.metricsCollection.at(Number(index)).getOrderBy(),
                sortOrder: Number(sortOrder)
            });
        },

        onExpanderClick: function (e) {
            const me = this;

            e.stopPropagation();
            const target = $(e.currentTarget);
            const row = target.parents('tr').toggleClass('expanded');
            const subordinateRows = row.nextUntil('.master-row').toggleClass('expanded');

            let newExpansions;
            const index = row.data('index');
            const expansions = getExpansions(me);
            let rerenderInline = false;
            if (_.includes(expansions, index)) {
                newExpansions = _.without(expansions, index);
                rerenderInline = true;
            } else {
                newExpansions = _.union(expansions, [index]);
                // NOTE if this is the first time we are expanding this row
                // then we need to do the rendering
                if (_.isEmpty(subordinateRows)) {
                    // NOTE we do this in a frame so that we don't lock the UI
                    // trying to render, and show the dimension rows
                    me.requestRenderedFrame(function () {
                        me._renderOnBoundsChange();
                        const dimensionRows = me.renderDimensionRows(me.rows, me.dimensionRows, index, true);

                        row.after(dimensionRows);

                        // NOTE we have to build popovers, tooltips in case
                        // some were rendered into the dimension rows
                        me.requestRenderedFrame(function () {
                            me.buildDescriptionPopovers();
                            me.$('[data-toggle="tooltip"]').tooltip();
                        });
                    });
                } else {
                    rerenderInline = true;
                }
            }

            me.setProps({
                rowExpansions: (_.isEmpty(newExpansions) ?
                    undefined : `(${newExpansions.join('.')})`)
            }, {
                silent: true
            });

            if (rerenderInline) {
                me._renderOnBoundsChange();
            }

            if (me.dashboardModel.expansionTracking) {
                RivalIQ.execute('tracking:push:auth', me.dashboardModel.expansionTracking, {
                    widgetId: me.dashboardModel.get('id'),
                    curationId: _.get(me.dashboardModel, ['curation', 'id'], me.dashboardModel.id)
                });
            }
        },

        _renderOnBoundsChange: function () {
            const me = this;

            me._setBounds(me.rows);

            const columns = me.getColumns(me.rows);
            _.each(columns, function (column, columnIdx) {
                const CellView = me.getCellView(me.rows, 0, columnIdx);
                if (CellView && CellView.updateCellsOnExpansion) {
                    const tableRows = me.ui.tbody.find('tr');
                    tableRows.each(function (trIdx, trEl) {
                        const $trEl = $(trEl);
                        let rowData;

                        if ($trEl.hasClass('subordinate-row')) {
                            const masterRowIdx = $trEl.data('master-index');
                            const subordinateRowIdx = $trEl.data('index');
                            rowData = me.dimensionRows[masterRowIdx][subordinateRowIdx];
                        } else {
                            const rowIdx = $trEl.data('index');
                            rowData = me.rows[rowIdx];
                        }

                        // It's possible that rowData is undefined if this is a dimensionViewMoreLink row.
                        if (rowData) {
                            const metric = me.getMetricModel(0, columnIdx);

                            const tdEl = $trEl.find(`td:nth-child(${columnIdx + 1})`);
                            CellView.updateCellsOnExpansion(metric, tdEl, rowData[columnIdx], me.bounds[columnIdx]);
                        }
                    });
                }
            });
        },

        _columnIndex: function (property) {
            const me = this;

            const metric = me.collection.find(function (m) {
                return m.get('property') === property;
            });

            return me.collection.indexOf(metric);
        },

        initialize: function () {
            const me = this;

            _.extend(me, _.pick(me.dashboardModel, 'metrics', 'metric', 'metricsCollection'));

            const visibleMobile = me.getOption('visibleMobile');
            if (visibleMobile && RivalIQ.utils.browser.isMobile()) {
                const metrics = _.reduce(me.metricsCollection.toJSON(), (memo, metric, i) => {
                    if (_.includes(visibleMobile, i)) {
                        memo.push(metric);
                    }
                    return memo;
                }, []);
                me.metricsCollection = new RivalIQ.collections.MetricDefinitions(metrics);
            }

            me._bindTableRowEvents();
        },

        _bindTableRowEvents: function () {
            const me = this;

            if (_.size(me.tableRowEvents)) {
                _.each(me.tableRowEvents, function (handler, selector) {
                    me.events[selector] = me._tableCellEventWrapper(handler);
                });
                me.delegateEvents();
            }
        },

        _tableCellEventWrapper: function (fcn) {
            return function (e) {
                const me = this;
                const tr = $(e.currentTarget).parents('tr');
                const idx = $(e.currentTarget).parents('td').index();

                const row = me.rows[tr.data('index')];
                const data = row[idx];

                if (_.isString(fcn)) {
                    me[fcn](e, data);
                } else if (_.isFunction()) {
                    fcn(e, data);
                }
            };
        },

        _getBrowserScrollTax: function () {
            let tax = 0;

            const browser = RivalIQ.utils.browser;
            if (browser.isEdge()) {
                tax = -2;
            } else if (browser.isWindows() && browser.isFirefox()) {
                // NOTE Firefox on Windows reports bad scroll dimensions
                // (see #14153)
                tax = -1;
            }

            return tax;
        },

        // TODO phantom remove when we have a better form of export
        // TODO check if this is still needed in the puppeteer world
        _getElementScroll: function (el) {
            const me = this;

            const browserTax = me._getBrowserScrollTax();

            const scroll = {
                height: el.scrollHeight + browserTax,
                width: el.scrollWidth + browserTax
            };

            return scroll;
        },

        checkEllipsibleContentForOverflow: function () {
            const me = this;

            me.$('.ellipse-content').each(function (idx, el) {
                const $el = $(el);
                let text = ($el.data('text') || $el.text());
                if (!$el.data('text')) {
                    $el.data('text', $el.text());
                }

                $el.text(text);

                let i = 0;
                let scroll = me._getElementScroll(el);

                while (scroll.height > el.clientHeight || scroll.width > el.clientWidth) {
                    text = `${text.slice(0, i - 3)}...`;
                    if (text === '...') {
                        break;
                    }
                    $el.text(text);

                    scroll = me._getElementScroll(el);
                    --i;
                }
            });
        }
    });

    const buildWidget = Marionette.Dashboard.models.Configuration.prototype.buildWidget;

    const Summary = RivalIQ.views.CollectionView.mixin({
        mixins: {
            props: Marionette.Dashboard.mixins.Props
        },
        className: 'row',
        childView: RivalIQ.views.LayoutView.mixin({
            className: function () {
                const me = this;

                // NOTE first check if a `className` is defined on the model...
                const className = me.model.get('className');
                if (className) {
                    return className;
                }

                // NOTE ...if not try to guess based on the number of items 🤷
                const summaryMetrics = me.dashboardModel.summaryMetrics;
                return ['col-sm', Math.max(12 / summaryMetrics.length, 3)].join('-');
            },
            regions: {
                widget: '.summary-widget-region'
            },
            mixins: {
                props: Marionette.Dashboard.mixins.Props
            },
            template: _.constant('<div class="summary-widget-region"/>'),
            onBeforeRenderLoaded: function () {
                const me = this;
                const curation = me.dashboardModel.curation;
                const widgetTypeId = (me.model.get('widgetTypeId') || RivalIQ.enums.widgetTypes.METRIC);
                const widget = buildWidget(curation, widgetTypeId);
                widget.setProps(_.omit(me.model.toJSON(), 'className', 'widgetTypeId'));
                me.widget.showWidget(widget);
            }
        }),
        childViewOptions: function (model) {
            const me = this;
            return _.defaults({
                dashboardModel: me.dashboardModel
            }, model.toJSON());
        }
    });

    widgets.SummarizedMetricTable = widgets.MetricTable.extend({
        regions: {
            summary: '.summary-metrics'
        },
        template: '#summarizedMetricTableTemplate',
        initialize: function () {
            const me = this;

            const getOption = me.summary.getOption;
            me.summary.getOption = function (opt) {
                const region = this;
                if (opt === 'allowMissingEl') {
                    return true;
                }
                return getOption.apply(region, arguments);
            };

            widgets.MetricTable.prototype.initialize.apply(me, arguments);
        },
        onBeforeRenderLoaded: function () {
            const me = this;
            me.summary.show(new Summary({
                collection: me.dashboardModel.summaryMetrics,
                dashboardModel: me.dashboardModel
            }));
        }
    });

    function isSupportedBenchmarkSource(metric) {
        const source = metric.get('source');
        const isSupportedSource = (source === RivalIQ.enums.sources.CROSS_CHANNEL ||
            _.includes(RivalIQ.enums.sourceDetails[RivalIQ.enums.sources.CROSS_CHANNEL].subSources, source));

        return isSupportedSource;
    }

    function shouldShowBenchmarksForCompaniesTable(dashboardModel) {

        const selectedBenchmarkCompareAgainst = dashboardModel.getProp('selectedBenchmarkCompareAgainst');

        if (selectedBenchmarkCompareAgainst) {
            const benchmarkCompareAgainst = RivalIQ.apps.Overview.utils.getSelectedBenchmarkCompareAgainst(selectedBenchmarkCompareAgainst);
            const benchmarkCompanies = RivalIQ.apps.Overview.utils.getCompaniesForFilter(benchmarkCompareAgainst.benchmarkCompaniesFilter, dashboardModel);

            // For this table, it only makes sense to show a benchmark row if there's more than one company in the benchmark (if there's just
            // one company, it'll already have a row in the table).
            return (benchmarkCompanies.length > 1);
        } else {
            return false;
        }
    }

    function shouldShowSummaryRowForCompaniesTable(dashboardModel) {
        const showCustomSummary = dashboardModel.getProp('showCustomSummary');
        return showCustomSummary;
    }

    function hasPrimaryDimensionSummaryRowAggregate(dashboardModel) {
        const primaryDimensionSummaryRowAggregate = dashboardModel.getProp('primaryDimensionSummaryRowAggregate');
        const primaryDimension = dashboardModel.getProp('primaryDimension');
        return primaryDimensionSummaryRowAggregate && primaryDimension && primaryDimension !== 'companyId';
    }

    function getPrimaryDimensionSummaryAggregate(dashboardModel, idx) {
        const primaryDimensionSummaryRowAggregate = dashboardModel.getProp('primaryDimensionSummaryRowAggregate');
        if (primaryDimensionSummaryRowAggregate === 'useMetricAggregate') {
            const metrics = dashboardModel.metricsCollection;
            const metric = metrics.at(idx);
            const companiesAggregate = metric.get('companyIdAggregate');
            const aggregate = companiesAggregate && companiesAggregate.substring('companies'.length).toLowerCase();
            return aggregate;
        } else {
            return primaryDimensionSummaryRowAggregate;
        }
    }

    const CompanyMetricsTableWidget = widgets.MetricTable.extend({

        getTableClassName: function () {
            const me = this;

            let tableClass = widgets.MetricTable.prototype.getTableClassName.call(me, arguments);

            const hasPartialDataWarning = me.collection.any(metric => {
                const calculationMetadata = metric.getCalculationMetadata();

                return _.some(calculationMetadata, (metadata) => {
                    return !_.isEmpty(_.get(metadata, ['missingSources']));
                });
            });

            if (hasPartialDataWarning) {
                tableClass += ' has-partial-data-warning';
            } else {
                tableClass += ' no-partial-data-warning';
            }

            return tableClass;
        },

        rowClass: function (cells, i) {
            const me = this;

            let rowClass = widgets.MetricTable.prototype.rowClass.apply(me, arguments);

            const metric = me.getMetricModel(i, 0);
            const freeDimensions = metric.getFreeDimensions();
            if (_.isEqual(freeDimensions, ['companyId'])) {
                if (cells[0][0] === me.getProp('selectedBenchmarkCompareAgainst')) {
                    rowClass += ' benchmark-row';
                }
                if (cells[0][0] === SUMMARY_ROW) {
                    rowClass += ' custom-summary-row';
                }

                if (i > 0) {
                    // If we've filtered the row before row i, the entry for that row will be null in me.rows.
                    const previousRow = me.rows[i - 1];
                    if (_.isNull(previousRow)) {
                        rowClass += ' ellipsed';
                    }
                }
            } else if (_.isEqual(freeDimensions, ['companySetId'])) {
                if (cells[0][0] === SUMMARY_ROW) {
                    rowClass += ' custom-summary-row';
                }
            }

            return rowClass;
        },

        loadMetrics: function (attributes) {
            const me = this;

            if (isDisallowedCompanySetAggregation(me)) {
                return $.when();
            }

            const fetches = widgets.MetricTable.prototype.loadMetrics.call(me, arguments);

            me.benchmarkMetrics = null;
            me.benchmarkPeriodValueMetrics = null;

            me.summaryRowMetrics = null;
            me.summaryRowPeriodValueMetrics = null;

            const selectedBenchmarkCompareAgainst = me.getProp('selectedBenchmarkCompareAgainst');
            if (selectedBenchmarkCompareAgainst) {
                const metrics = me.metricsCollection;

                if (shouldShowBenchmarksForCompaniesTable(me)) {
                    const benchmarkCompareAgainst = RivalIQ.apps.Overview.utils.getSelectedBenchmarkCompareAgainst(selectedBenchmarkCompareAgainst);
                    const benchmarkMetrics = metrics.map(function (model) {
                        return model.build(_.extend({}, attributes, {
                            format: undefined, // unset this otherwise the metric model will assume you want "pdf" as the formatted value for this metric.
                            aggregate: benchmarkCompareAgainst.aggregate,
                            benchmarkCompaniesFilter: benchmarkCompareAgainst.benchmarkCompaniesFilter,
                            dimensions: _.without(model.get('dimensions'), 'companyId')
                        }), {
                            clobberDimensions: true
                        });
                    });

                    const benchmarkFetches = new RivalIQ.collections.MetricDefinitions(benchmarkMetrics);
                    const benchmarkPeriodValueMetrics = new RivalIQ.collections.MetricDefinitions(
                        metrics.reduce(function (memo, metric, idx) {
                            if (metric.get('useUniversalMetricsApi') && metric.get('showSparkline')) {
                                let interval = me.getProp('interval');
                                if (me.getProp('useAutoTimeInterval') || _.isUndefined(interval)) {
                                    me.setProps('useAutoTimeInterval', true);
                                    const timePeriod = me.getProp('timePeriod');
                                    interval = RivalIQ.metric.timePeriod.getDefaultInterval(timePeriod);
                                }
                                memo.push(metric.build({
                                    _parent: benchmarkMetrics[idx].getIdentityForUniversalApi(),
                                    aggregate: benchmarkCompareAgainst.aggregate,
                                    benchmarkCompaniesFilter: benchmarkCompareAgainst.benchmarkCompaniesFilter,
                                    dimensions: _.without(metric.get('dimensions'), 'companyId').concat([interval])
                                }, {
                                    clobberDimensions: true
                                }));
                            }
                            return memo;
                        }, [])
                    );

                    return $.when(fetches, benchmarkFetches.fetch(attributes), benchmarkPeriodValueMetrics.fetch(attributes))
                        .then(function (res, benchmarkMetrics, benchmarkPeriodValueMetrics) {

                            // if all metrics with no property === 'name' are private, elide.
                            const hasNonPrivateBenchmark = benchmarkMetrics.any(metric => {
                                return metric.get('property') !== 'name' && isSupportedBenchmarkSource(metric);
                            });

                            const hasMultiPrivateChannelAdvanced = RivalIQ.state.plan.get('has_multi_handle_private_advanced');

                            if (hasNonPrivateBenchmark || hasMultiPrivateChannelAdvanced) {
                                res.benchmarkMetrics = benchmarkMetrics.models;
                                res.benchmarkPeriodValueMetrics = benchmarkPeriodValueMetrics.models;

                            } else {
                                res.benchmarkMetrics = null;
                                res.benchmarkPeriodValueMetrics = null;
                            }

                            return res;
                        });
                }
            } else if (shouldShowSummaryRowForCompaniesTable(me)) {
                const metrics = me.metricsCollection;

                let summaryRowMetrics;

                if (hasPrimaryDimensionSummaryRowAggregate(me)) {
                    const primaryDimension = me.getProp('primaryDimension');
                    summaryRowMetrics = metrics.reduce(function (memo, model, idx) {
                        if (idx === 0) {
                            return memo;
                        }
                        memo.push(model.build({
                            format: undefined,
                            [`${primaryDimension}Aggregate`]: getPrimaryDimensionSummaryAggregate(me, idx),
                            dimensions: _.without(model.get('dimensions'), primaryDimension),
                            over: primaryDimension
                        }, {
                            clobberDimensions: true
                        }));

                        return memo;
                    }, []);
                } else {
                    const customSummaryMetrics = me.getProp('customSummaryMetrics');

                    if (_.size(customSummaryMetrics) !== metrics.length - 1) { // -1 is for the name metric
                        // this shouldn't happen, but if it does we can't really know which metric gets which summary so bail.
                        RivalIQ.logger.warn(`table_loadMetrics error size of customSummaryMetrics does not match size of metrics ${_.size(customSummaryMetrics)} vs ${metrics.length}`);
                        return fetches;
                    }

                    summaryRowMetrics = metrics.reduce(function (memo, model, idx) {
                        if (idx === 0) {
                            return memo;
                        }
                        const summary = customSummaryMetrics[idx - 1];
                        if (summary) {
                            const benchmarkCompareAgainst = RivalIQ.apps.Overview.utils.getSelectedBenchmarkCompareAgainst(summary);
                            memo.push(model.build({
                                format: undefined, // unset this otherwise the metric model will assume you want "pdf" as the formatted value for this metric.
                                aggregate: benchmarkCompareAgainst.aggregate,
                                benchmarkCompaniesFilter: benchmarkCompareAgainst.benchmarkCompaniesFilter,
                                dimensions: _.without(model.get('dimensions'), 'companyId')
                            }, {
                                clobberDimensions: true
                            }));
                        }

                        return memo;
                    }, []);
                }

                if (_.isEmpty(summaryRowMetrics)) {
                    // this would happen if all summaries are set to `none`, so there's no work to do.
                    return fetches;
                }

                const summaryRowFetches = new RivalIQ.collections.MetricDefinitions(summaryRowMetrics);

                let summaryRowPeriodValueMetrics;
                if (hasPrimaryDimensionSummaryRowAggregate(me)) {
                    const primaryDimension = me.getProp('primaryDimension');
                    summaryRowPeriodValueMetrics = new RivalIQ.collections.MetricDefinitions(
                        metrics.reduce(function (memo, metric, idx) {
                            if (idx !== 0 && metric.get('useUniversalMetricsApi') && metric.get('showSparkline')) {
                                let interval = me.getProp('interval');
                                if (me.getProp('useAutoTimeInterval') || _.isUndefined(interval)) {
                                    me.setProps('useAutoTimeInterval', true);
                                    const timePeriod = me.getProp('timePeriod');
                                    interval = RivalIQ.metric.timePeriod.getDefaultInterval(timePeriod);
                                }
                                memo.push(metric.build({
                                    _parent: metric.getIdentityForUniversalApi(),
                                    [`${primaryDimension}Aggregate`]: getPrimaryDimensionSummaryAggregate(me, idx),
                                    over: primaryDimension,
                                    dimensions: _.without(metric.get('dimensions'), primaryDimension).concat([interval])
                                }, {
                                    clobberDimensions: true
                                }));
                            }
                            return memo;
                        }, [])
                    );
                } else {
                    const customSummaryMetrics = me.getProp('customSummaryMetrics');
                    summaryRowPeriodValueMetrics = new RivalIQ.collections.MetricDefinitions(
                        metrics.reduce(function (memo, metric, idx) {
                            if (idx !== 0 && metric.get('useUniversalMetricsApi') && metric.get('showSparkline')) {
                                const summary = customSummaryMetrics[idx - 1];
                                if (summary) {
                                    const benchmarkCompareAgainst = RivalIQ.apps.Overview.utils.getSelectedBenchmarkCompareAgainst(summary);
                                    let interval = me.getProp('interval');
                                    if (me.getProp('useAutoTimeInterval') || _.isUndefined(interval)) {
                                        me.setProps('useAutoTimeInterval', true);
                                        const timePeriod = me.getProp('timePeriod');
                                        interval = RivalIQ.metric.timePeriod.getDefaultInterval(timePeriod);
                                    }
                                    memo.push(metric.build({
                                        _parent: metric.getIdentityForUniversalApi(),
                                        aggregate: benchmarkCompareAgainst.aggregate,
                                        benchmarkCompaniesFilter: benchmarkCompareAgainst.benchmarkCompaniesFilter,
                                        dimensions: _.without(metric.get('dimensions'), 'companyId').concat([interval])
                                    }, {
                                        clobberDimensions: true
                                    }));
                                }
                            }
                            return memo;
                        }, [])
                    );
                }

                return $.when(fetches, summaryRowFetches.fetch(attributes), summaryRowPeriodValueMetrics.fetch(attributes))
                    .then(function (res, summaryRowMetrics, summaryRowPeriodValueMetrics) {

                        // if all metrics with no property === 'name' are private, elide.
                        const hasNonPrivateBenchmark = summaryRowMetrics.any(metric => {
                            return metric.get('property') !== 'name' && isSupportedBenchmarkSource(metric);
                        });

                        const hasMultiPrivateChannelAdvanced = RivalIQ.state.plan.get('has_multi_handle_private_advanced');

                        if (hasPrimaryDimensionSummaryRowAggregate(me)) {
                            res.summaryRowMetrics = [null].concat(summaryRowMetrics.models);
                            res.summaryRowPeriodValueMetrics = [null].concat(summaryRowPeriodValueMetrics.models);
                        } else if (hasNonPrivateBenchmark || hasMultiPrivateChannelAdvanced) {

                            res.summaryRowMetrics = _.fill(Array(metrics.length), null);
                            res.summaryRowPeriodValueMetrics = _.fill(Array(metrics.length), null);

                            let currSummaryIdx = 0;
                            let currSummaryPeriodValuesIdx = 0;
                            const customSummaryMetrics = me.getProp('customSummaryMetrics');
                            _.each(metrics.models, (metric, idx) => {
                                if (idx === 0) {
                                    return;
                                }

                                const summary = customSummaryMetrics[idx - 1];
                                if (summary) {
                                    res.summaryRowMetrics[idx] = summaryRowMetrics.models[currSummaryIdx++];

                                    const metric = me.metricsCollection.at(idx);
                                    if (metric.get('useUniversalMetricsApi') && metric.get('showSparkline')) {
                                        res.summaryRowPeriodValueMetrics[idx] = summaryRowPeriodValueMetrics.models[currSummaryPeriodValuesIdx++];
                                    }
                                }
                            });

                        } else {
                            res.summaryRowMetrics = null;
                            res.summaryRowPeriodValueMetrics = null;
                        }

                        return res;
                    });
            }

            return fetches;
        },

        _getRawColumnData: function () {
            const me = this;

            const companyColumns = widgets.MetricTable.prototype._getRawColumnData.call(me, arguments);
            if (me.benchmarkMetrics) {
                const selectedBenchmarkCompareAgainst = me.getProp('selectedBenchmarkCompareAgainst');
                me.benchmarkMetrics.map(function (benchmarkMetric, columnIdx) {
                    let benchmarkValue = null;
                    if (columnIdx === 0) {
                        benchmarkValue = selectedBenchmarkCompareAgainst;
                    } else if (benchmarkMetric) {
                        benchmarkValue = _.get(benchmarkMetric.getValue({
                            format: false
                        }), [0, 0]);
                    }

                    companyColumns[columnIdx] = companyColumns[columnIdx].concat([[selectedBenchmarkCompareAgainst, benchmarkValue]]);
                });
            } else if (me.summaryRowMetrics) {
                _.each(me.summaryRowMetrics, (summaryRowMetric, columnIdx) => {
                    let summaryValue = null;
                    if (columnIdx === 0) {
                        summaryValue = SUMMARY_ROW;
                    } else if (summaryRowMetric) {
                        summaryValue = _.get(summaryRowMetric.getValue({
                            format: false
                        }), [0, 0]);
                    }

                    companyColumns[columnIdx] = companyColumns[columnIdx].concat([[SUMMARY_ROW, summaryValue]]);
                });
            }

            return companyColumns;
        },

        _getRows: function (sortColumns, sortOrder, sortFunctions) {
            const me = this;

            const columns = me._getRawColumnData();

            let rows = columns;

            if (!me.invert) {
                rows = _.zip.apply(null, columns);
                rows = rows.sort(sortRow(sortColumns, sortOrder, sortFunctions));
            }

            // Before we filter the rows, we need to compute the ranks for each company.
            const selectedBenchmarkCompareAgainst = me.getProp('selectedBenchmarkCompareAgainst');
            let lastRank = 0;
            me.companyIdToRankMapping = _.reduce(me._filterRows(rows, -1), function (memo, row, idx) {
                const rowId = row[0][0];
                // only companies count in the rankings
                if (rowId !== selectedBenchmarkCompareAgainst && rowId !== SUMMARY_ROW) {
                    lastRank++;
                }
                memo[rowId] = lastRank;

                return memo;
            }, []);

            if (selectedBenchmarkCompareAgainst) {
                const benchmarkRow = _.find(rows, function (row) {
                    return (row[0][0] === selectedBenchmarkCompareAgainst);
                });

                const sortMetric = me.collection.at(_.head(sortColumns));

                // If we're sorting by a metric that doesn't support benchmarking, put the benchmark rows at the end of the table. See #27541
                if (sortMetric && benchmarkRow) {
                    const source = sortMetric.get('source');
                    const isSupportedSource = (source === RivalIQ.enums.sources.CROSS_CHANNEL ||
                        _.includes(RivalIQ.enums.sourceDetails[RivalIQ.enums.sources.CROSS_CHANNEL].subSources, source));

                    if (!isSupportedSource || sortMetric.get('property') === 'name') {
                        rows = _.without(rows, benchmarkRow);
                        rows.push(benchmarkRow);
                    }
                }
            }

            // The summary row always goes at the end
            if (me.getProp('showCustomSummary')) {
                const summaryRow = _.find(rows, function (row) {
                    return (row[0][0] === SUMMARY_ROW);
                });

                if (summaryRow) {
                    rows = _.without(rows, summaryRow);
                    rows.push(summaryRow);
                }
            }

            return me._filterRows(rows);
        },

        renderCell: function (data, columnIndex, rowIndex) {
            const me = this;

            if (columnIndex > 0 && data && data[0] === SUMMARY_ROW && !hasPrimaryDimensionSummaryRowAggregate(me)) {
                // if this custom summary cell is none, just return an empty cell.
                const customSummary = _.get(me.getProp('customSummaryMetrics'), [columnIndex - 1]);
                if (!customSummary) {
                    return '<td></td>';
                }
            }

            if (columnIndex === 0 && _.isEqual(me.getMetricModel(rowIndex, columnIndex).getFreeDimensions(), ['companyId'])) {

                const template = Marionette.TemplateCache.get('#metricTableRankCellTemplate');
                let nameColumnData;

                if (data[0] === me.getProp('selectedBenchmarkCompareAgainst')) {
                    nameColumnData = {
                        benchmarkCompareAgainst: RivalIQ.apps.Overview.utils.getSelectedBenchmarkCompareAgainst(data[0])
                    };
                } else if (data[0] === SUMMARY_ROW) {
                    return Marionette.TemplateCache.get('#metricTableSummaryCellTemplate')();
                } else {
                    const companyId = data[0];
                    const company = RivalIQ.state.landscape.getCompanyById(companyId);
                    nameColumnData = _.extend({
                        benchmarkCompareAgainst: null,
                        href: `/company/${companyId}`,
                        icon_url: company.getIcon(),
                        isFocus: (company.id === me.getProp('focusCompanyId')),
                        companyRank: me.companyIdToRankMapping[companyId]
                    }, company.toJSON());
                }

                return template(nameColumnData);
            }

            return widgets.MetricTable.prototype.renderCell.apply(me, arguments);
        }
    });

    widgets.TableWidget = widgets.ChartWidget.mixin({
        mixins: {
            ExportInHD: App.mixins.ExportHD
        },

        hasFooter: _.constant(true),
        footerView: App.views.WarningLegendFooterView,

        getFooterView: function () {
            const me = this;

            if (isDisallowedCompanySetAggregation(me)) {
                return App.views.PanelFooter;
            }

            return widgets.ChartWidget.prototype.getFooterView.apply(me);
        },

        shouldShowExport: function () {
            const me = this;

            if (isDisallowedCompanySetAggregation(me)) {
                return false;
            }

            return widgets.ChartWidget.prototype.shouldShowExport.apply(me);
        },

        propValues: _.defaults({
            columnWidth: function () {
                const me = this;

                // if we're showing more than 5 metrics (name metric + 4 custom), then we don't
                // want to allow half-width tables. Thus, the only only valid columnWidth in that
                // case is 12 (a.k.a., full width).
                if (_.size(me.metrics) > COLUMN_MAX[RivalIQ.enums.orientation.PORTRAIT][6]) {
                    return [12];
                }

                if (me.getProp('primaryDimension') === 'companySetId') {
                    return [12];
                }
            }
        }, widgets.ChartWidget.prototype.propValues),

        augmentMetrics: function (props) {
            const me = this;

            const defs = _.clone(props.metrics);
            // TODO we dont work w/ multiple table dimensions yet
            // A better pattern would probably be passing the dimensions array
            // to the table itself and then building passed metrics w those
            // dimensions
            if (props.dimensionColumns) {

                // not all props.metrics will be shown, it's possible that some channels are hidden.
                // Find the best candidate for the name column; prefer a public metric, but if there's
                // only private metrics, use the first one.
                let visiblePublicMetric;
                let visiblePrivateMetric;
                _.each(props.metrics, function (metric) {
                    if (RivalIQ.metric.access.channel(metric, props)) {
                        const source = RivalIQ.metric.property.source(metric);
                        if (RivalIQ.enums.sourceDetails[source].privateData) {
                            visiblePrivateMetric = metric;
                        } else {
                            visiblePublicMetric = metric;
                        }
                    }
                });

                const nameMetricTemplate = visiblePublicMetric || visiblePrivateMetric;
                if (nameMetricTemplate) {
                    const col = _.extend({}, nameMetricTemplate, {
                        property: 'name',
                        showSparkline: false,
                        mustInclude: function (data, metric) {
                            return (Number(_.head(data)) === metric.focusCompanyId) ||
                                _.find(RivalIQ.enums.benchmarkCompareAgainstOptions, {
                                    id: _.head(data)
                                }) ||
                                _.head(data) === SUMMARY_ROW;
                        }
                    });

                    defs.unshift(col);
                }
            }

            // Ensure sort column is in kept set
            if (!_.isUndefined(props.orderBy) && props.columnLimit && props.orderBy >= props.columnLimit && defs[props.orderBy]) {
                const sortMetric = defs[props.orderBy];
                props.orderBy = props.columnLimit - 1;
                defs[props.orderBy] = sortMetric;
            }

            // If the secondary dimension is companyId, be sure to override the dimensionView; otherwise, we'll get the default CompanyRank
            // view, which will add a rank that we don't want.
            if (props.dimension === 'companyId') {
                defs[0].dimensionView = RivalIQ.apps.SocialPosts.widgets.CompanyCell;
            }

            if (props.companyId) {
                props.companyId = props.focusCompanyId;
            }

            me.metrics = me.buildMetrics(props, defs);
            me.metricsCollection = new RivalIQ.collections.MetricDefinitions(me.metrics);
        },

        isSupported: function (props) {
            return !_.isEmpty(props.metrics);
        },

        // Tables dont get the default description generation, presumably b/c
        // the description would be too long
        description: undefined,

        processProps: function (props, changed) {
            const me = this;

            _.defaults(props, {
                sortable: true,
                sortOrder: 1,
                useUniversalMetricsApi: me.curation.isCustomDashboard
            });

            const propsToPullFromQueryParams = [
                'limit', 'sortOrder', 'orderBy'
            ];

            const queryParams = _.pick(me.getQueryParams(), propsToPullFromQueryParams);
            // we want to always look at query params for the sort props; however, if we're in the middle of changing
            // the props, then processProps will be called the new sort props in changed (before they've been put on
            // the query params). In that case, we should use the props from changed.
            _.extend(props, queryParams, _.pick(changed, propsToPullFromQueryParams));

            // TODO we should remove this when we are confident scheduled
            // exports are configured correctly (i.e., they have a limit of
            // `-1` specified in their URL) - @crteal @vjchem 2017/03/27
            if (me.isFocus() && !_.has(queryParams, 'limit') && _.isUndefined(props.limit)) {
                props.limit = -1;
            }

            if (props.metrics) {
                me.augmentMetrics(props);
            } else if (me.rebuildMetricsCollection) {
                me.metricsCollection = me.rebuildMetricsCollection(props);
            }
            return props;
        },

        getVisualExportLimit: function (props) {
            let limit = props.limit;

            if (!_.isUndefined(limit)) {
                if (limit === -1) {
                    limit = MAX_VISUAL_EXPORT_ROWS;
                }
                limit = Math.min(limit, MAX_VISUAL_EXPORT_ROWS);
            }

            return limit;
        },

        processExportProps: function (props, changed) {
            const me = this;

            const orientation = me.getProp('orientation') || RivalIQ.enums.orientation.LANDSCAPE;
            const columnWidth = me.getProp('columnWidth') || 12;
            props.columnLimit = _.get(COLUMN_MAX, [orientation, columnWidth]);
            props.limit = me.getVisualExportLimit(props);
            return me.processProps(props, changed);
        },

        getExportMetricDefinitions: function (format) {
            const me = this;
            return me.metricsCollection.invoke('serialize', format);
        },

        csvExportProps: function () {
            const me = this;

            const definitions = me.getExportMetricDefinitions('csv');
            // NOTE in CSV exports we do not honor limits; export it all
            let metrics = _.reduce(definitions, function reducer(memo, definition) {
                if (_.isArray(definition)) {
                    return _.reduce(definition, reducer, memo);
                }
                return memo.concat(_.defaults({
                    limit: -1
                }, definition));
            }, []);


            const csvConfig = {
                type: 'metric',
                includeDimensionColumn: false,
                nameDimensions: me.getProp('nameDimensions')
            };

            if (_.isEqual(RivalIQ.metric.getFreeDimensions(metrics[0]), ['companyId'])) {
                csvConfig.wideRow = true;
                csvConfig.includeDimensionColumn = true;

                // We don't want the name metric since we're using the `wideRow` csv export, which will inclide the dimension column anyway.
                if (metrics[0].property === 'name') {
                    metrics = _.tail(metrics);
                }

                if (shouldShowBenchmarksForCompaniesTable(me)) {

                    const benchmarkCompareAgainst = RivalIQ.apps.Overview.utils.getSelectedBenchmarkCompareAgainst(me.getProp('selectedBenchmarkCompareAgainst'));
                    metrics = metrics.concat(_.map(metrics, function (metric) {
                        const benchmarkDimensions = _.without(metric.dimensions || [], 'companyId');
                        return _.defaults({
                            extraFreeDimensions: {
                                companyId: benchmarkCompareAgainst.name
                            },
                            aggregate: benchmarkCompareAgainst.aggregate,
                            benchmarkCompaniesFilter: benchmarkCompareAgainst.benchmarkCompaniesFilter,
                            dimensions: benchmarkDimensions,
                            useMatchingMetricColumn: true
                        }, metric);
                    }));
                } else if (shouldShowSummaryRowForCompaniesTable(me)) {

                    const customSummaryMetrics = _.uniq(me.getProp('customSummaryMetrics'));
                    _.each(customSummaryMetrics, customSummaryMetric => {
                        if (!customSummaryMetric) {
                            return;
                        }
                        const customSummary = RivalIQ.apps.Overview.utils.getSelectedBenchmarkCompareAgainst(customSummaryMetric);
                        metrics = metrics.concat(_.map(metrics, function (metric) {
                            const benchmarkDimensions = _.without(metric.dimensions || [], 'companyId');
                            return _.defaults({
                                extraFreeDimensions: {
                                    companyId: customSummary.name
                                },
                                aggregate: customSummary.aggregate,
                                benchmarkCompaniesFilter: customSummary.benchmarkCompaniesFilter,
                                dimensions: benchmarkDimensions,
                                useMatchingMetricColumn: true
                            }, metric);
                        }));
                    });
                }
            }

            csvConfig.metric = metrics;

            return csvConfig;
        },

        // Props that should be reflected in full page export
        getPropsForCurationExport: function () {
            const me = this;

            const exportProps = ['sortOrder', 'orderBy'];
            const widgetProps = _.pick(me.getProps(), exportProps);
            const configurationProps = _.pick(me.configurationProps, exportProps);

            const changedKeys = _.reduce(widgetProps, function (memo, value, key) {
                const configurationProp = _.get(configurationProps, key);
                return (value !== configurationProp ? memo.concat(key) : memo);
            }, []);

            // NOTE only if the widget `props` differ from the curation's
            // configuration `props` should we return them (see #20313)
            return _.pick(widgetProps, changedKeys);
        },

        view: CompanyMetricsTableWidget,
        // No descriptions for tables
        getSlideDescriptions: _.noop,

        viewMorePath: function () {
            const me = this;
            return me.getPath({
                limit: -1,
                orderBy: me.getProp('orderBy'),
                sortOrder: me.getProp('sortOrder')
            });
        },

        allowPageBreaks: function () {
            const me = this;
            // NOTE in "details" export we want to support page breaking
            return me.isFocus();
        }
    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.METRIC_TABLE, widgets.TableWidget);

    widgets.SummarizedTableWidget = widgets.TableWidget.extend({
        view: widgets.SummarizedMetricTable,
        processProps: function (props) {
            const me = this;
            widgets.TableWidget.prototype.processProps.apply(me, arguments);
            me.summaryMetrics = me.getSummaryMetrics(props);
        },
        getSummaryMetrics: function (props) {
            const summaryMetrics = (props.summaryMetrics || []);
            const collection = new Backbone.Collection(summaryMetrics);
            return collection;
        }
    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.METRIC_TABLE_SUMMARIZED, widgets.SummarizedTableWidget);

    /*
     * A table where the metrics are listed vertically and dimension across the
     * columns headers
     *
     * NOTE only supports a single free dimension
     */
    const VerticalTable = widgets.TableWidget.extend({
        view: widgets.MetricTable.extend({
            invert: true,
            displayMetricCellViews: false
        }),
        propValues: {
            // TODO this is only true if the free dimension column is timeAnalysisMode
            timeAnalysisMode: []
        },
        csvExportProps: function () {
            const me = this;
            return _.defaults({
                invert: true,
                includeDimensionColumn: true
            }, widgets.TableWidget.prototype.csvExportProps.call(me));
        }
    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.METRIC_TABLE_VERTICAL, VerticalTable);

    const CompanyTableView = widgets.MetricTable.extend({
        invert: true,

        className: function () {
            const me = this;

            return `${widgets.MetricTable.prototype.className.call(me)} company-table`;
        },

        // NOTE we need to filter out `DAILY_VALUES` from columns
        _filterRows: function (rows) {
            const me = this;
            const filtered = widgets.MetricTable.prototype._filterRows.call(me, rows);
            return _.map(filtered, function (row) {
                return _.reduce(row, function (memo, column) {
                    const timeAnalysisMode = _.head(column);
                    if (timeAnalysisMode !== RivalIQ.enums.timeAnalysisMode.DAILY_VALUES) {
                        memo.push(column);
                    }
                    return memo;
                }, []);
            });
        },

        loadMetrics: function (attributes) {
            const me = this;

            // focus company table always displays sparkline. Since we are using MetricTable loadMetrics(),
            // we need to add showSparkline attributes to correctly fetch daily values.
            for (const metric of me.metricsCollection.models) {
                metric.attributes.showSparkline = true;
            }
            const fetches = widgets.MetricTable.prototype.loadMetrics.call(me, arguments);

            me.benchmarkMetrics = null;
            const selectedBenchmarkCompareAgainst = me.getProp('selectedBenchmarkCompareAgainst');
            if (selectedBenchmarkCompareAgainst) {
                const metrics = me.metricsCollection;

                const benchmarkCompareAgainst = RivalIQ.apps.Overview.utils.getSelectedBenchmarkCompareAgainst(selectedBenchmarkCompareAgainst);

                // Only get benchmark data if it'll be more than just be the focus company or no company.
                let showBenchmark;
                const benchmarkCompanies = RivalIQ.apps.Overview.utils.getCompaniesForFilter(benchmarkCompareAgainst.benchmarkCompaniesFilter, me);
                if (benchmarkCompareAgainst.benchmarkCompaniesFilter === RivalIQ.enums.benchmarkCompaniesFilters.exclude_focus_company.id) {
                    showBenchmark = benchmarkCompanies.length > 0;
                } else {
                    showBenchmark = benchmarkCompanies.length > 1;
                }

                if (showBenchmark) {
                    const timeAnalysisModes = [
                        RivalIQ.enums.timeAnalysisMode.MAIN_PERIOD,
                        RivalIQ.enums.timeAnalysisMode.COMPARE_PERIOD,
                        RivalIQ.enums.timeAnalysisMode.DELTA_ABSOLUTE,
                        RivalIQ.enums.timeAnalysisMode.DELTA_RELATIVE
                    ];

                    const benchmarkMetrics = metrics.reduce(function (memo, model) {
                        _.each(timeAnalysisModes, function (timeAnalysisMode) {
                            memo.push(model.build({
                                format: undefined, // unset this otherwise the metric model will assume you want "pdf" as the formatted value for this metric.
                                timeAnalysisMode: timeAnalysisMode,
                                aggregate: benchmarkCompareAgainst.aggregate,
                                benchmarkCompaniesFilter: benchmarkCompareAgainst.benchmarkCompaniesFilter,
                                dimensions: _.without(model.get('dimensions'), 'companyId')
                            }, {
                                clobberDimensions: true
                            }));
                        });

                        return memo;
                    }, []);

                    const benchmarkFetches = new RivalIQ.collections.MetricDefinitions(benchmarkMetrics);

                    return $.when(fetches, benchmarkFetches.fetch(attributes))
                        .then(function (res, benchmarkMetrics) {
                            res.benchmarkMetrics = _.times(metrics.length, function (metricIdx) {
                                const rowBenchmarkData = _.reduce(timeAnalysisModes, function (memo, timeAnalysisMode, idx) {
                                    memo[timeAnalysisMode] = benchmarkMetrics.at(metricIdx * timeAnalysisModes.length + idx);
                                    return memo;
                                }, {});

                                return rowBenchmarkData;
                            });
                            return res;
                        });
                }
            }

            return fetches;
        },

        _renderSparkLineCell: function (metric, data, rowIndex) {
            const me = this;

            const dailyValues = me.periodValues.models[rowIndex].data.map(periodValues => periodValues[1]);

            const sparklineEl = RivalIQ.graph.renderSparkline(dailyValues, null, {
                width: RivalIQ.graph.wideSparklineWidth
            });

            let tooltip = null;
            if (RivalIQ.metric.property.isNumeric(metric.attributes)) {
                let tipValue = '--';
                if (_.isNumber(_.last(data))) {
                    tipValue = metric.formatExpanded(data);
                }

                const privateDataNotConnectedTooltip = me._getPrivateDataNotConnectedTooltip(metric, metric.get('companyId'));
                if (privateDataNotConnectedTooltip) {
                    tooltip = privateDataNotConnectedTooltip;
                } else {
                    const showSourceIcons = me.dashboardModel.getCuration().isCustomDashboard;
                    const tooltipTemplate = Marionette.TemplateCache.get('#tableCellTooltip');
                    tooltip = tooltipTemplate({
                        name: metric.getName(),
                        value: tipValue,
                        sourceIcon: (showSourceIcons ? RivalIQ.enums.sourceDetails[metric.getSource()].iconSvg : null)
                    });
                }
            }

            const sparklineCellTemplate = Marionette.TemplateCache.get('#metricTableSparklineTableCell');

            const sparklineCell = sparklineCellTemplate({
                tooltip: tooltip,
                sparklineHtml: _.get(sparklineEl, 'outerHTML'),
                value: metric.format(data)
            });

            return cellTemplate({
                className: 'metric-table-cell sparkline-metric-table-cell',
                value: sparklineCell
            });
        },

        getBenchmarkData: function (timeAnalysisMode, columnIndex, rowIndex) {
            const me = this;

            const benchmarkData = _.get(me.benchmarkMetrics, [me.invert ? rowIndex : columnIndex, timeAnalysisMode]);
            return benchmarkData;
        },

        _showPrivateNoPresenceView: function () {
            const me = this;

            // handle no data because of missing permissions.
            const widgetInstanceState = me.dashboardModel.getWidgetInstanceState();
            const metrics = _.get(me.dashboardModel, ['metrics']);
            if (widgetInstanceState && metrics && metrics) {
                const sources = _(metrics)
                    .map(metric => {
                        return RivalIQ.metric.property.source(metric);
                    })
                    .uniq()
                    .value();

                const companyId = me.getProp('companyId');
                const companyHasPrivatePresence = companyId && _.find(widgetInstanceState.companies, {
                    id: companyId
                });
                if (sources.length === 1 && RivalIQ.enums.sourceDetails[sources[0]].privateData && widgetInstanceState.sources[sources[0]] && !companyHasPrivatePresence) {
                    const View = RivalIQ.apps.Insights.views.getNoPresenceView(sources[0]);
                    const view = new View({
                        dashboardModel: me.dashboardModel
                    });
                    const html = view.render().el;

                    me.attachElContent(html);
                    return true;
                }
            }
        },

        _renderBenchmarkCurrentCell: function (metric, data, benchmarkMetric) {
            const me = this;

            let tipValue = '--';
            if (_.isNumber(_.last(data))) {
                tipValue = metric.formatExpanded(data);
            }
            const benchmarkCompareAgainst = RivalIQ.apps.Overview.utils.getSelectedBenchmarkCompareAgainst(me.getProp('selectedBenchmarkCompareAgainst'));
            const tooltipTemplate = Marionette.TemplateCache.get('#tableWithBenchmarkCellTooltip');
            const tooltip = tooltipTemplate({
                name: metric.getName(),
                value: tipValue,
                benchmarkData: benchmarkMetric.getValue(),
                benchmarkName: benchmarkCompareAgainst.name
            });

            const cellTemplate = Marionette.TemplateCache.get('#metricTableBenchmarkCurrentTableCell');

            const focusValue = _.last(data) || 0;
            const benchmarkValue = _.get(benchmarkMetric.getValue({
                format: false
            }), [0, 0]) || 0;

            const maxWidth = _.max([focusValue, benchmarkValue]);
            const focusCompanyWidth = 100 * focusValue / maxWidth;
            const benchmarkWidth = 100 * benchmarkValue / maxWidth;

            return cellTemplate({
                focusCompanyWidth: focusCompanyWidth,
                value: metric.format(data),
                benchmarkWidth: benchmarkWidth,
                benchmarkValue: benchmarkMetric.getValue(),
                tooltip: tooltip
            });
        },

        _renderBenchmarkCompareCell: function (metric, data, benchmarkMetric, className, columnIndex) {
            const me = this;

            className += ' benchmark-cell';

            const tooltipTemplate = Marionette.TemplateCache.get('#tableWithBenchmarkCellTooltip');
            const benchmarkCompareAgainst = RivalIQ.apps.Overview.utils.getSelectedBenchmarkCompareAgainst(me.getProp('selectedBenchmarkCompareAgainst'));
            const tipValue = (_.isNumber(_.last(data)) ? metric.formatExpanded(data) : '--');
            let benchmarkDataValue = benchmarkMetric.getValue();
            let metricValue = metric.format(data);

            let tooltip = tooltipTemplate({
                name: metric.getName(),
                value: tipValue,
                benchmarkData: benchmarkDataValue,
                benchmarkName: benchmarkCompareAgainst.name
            });

            const timeAnalysisMode = _.head(data);
            const calculationMetadata = metric.getCalculationMetadata(timeAnalysisMode);
            const incompleteDataDetails = RivalIQ.utils.incompleteData.getIncompleteDataPopover('companyId', metric.get('companyId'), metric.build({
                timeAnalysisMode: timeAnalysisMode
            }), calculationMetadata);
            const partialDataPopover = _.get(incompleteDataDetails, ['popover']);
            if (partialDataPopover && _.isNull(_.last(data))) {
                metricValue = null;
                tooltip = null;
            }

            const benchmarkIncompleteDataDetails = RivalIQ.utils.incompleteData.getBenchmarkIncompleteDataDetails(benchmarkMetric.getCalculationMetadata(), benchmarkMetric, benchmarkCompareAgainst);
            let benchmarkPartialDataPopover;
            if (_.get(benchmarkIncompleteDataDetails, ['dataNotAvailable']) && metric.get('source') === RivalIQ.enums.sources.CROSS_CHANNEL) {
                benchmarkPartialDataPopover = benchmarkIncompleteDataDetails.dataNotAvailableTooltip;
                benchmarkDataValue = null;
            }

            const cellWithBenchmarkTemplateWithTip = Marionette.TemplateCache.get('#metricTableBenchmarkTableCell');
            return cellWithBenchmarkTemplateWithTip({
                className: className,
                value: metricValue,
                benchmarkData: benchmarkDataValue,
                tooltip: tooltip,
                partialDataPopover: partialDataPopover || null,
                benchmarkPartialDataPopover: benchmarkPartialDataPopover || null,
                columnIndex: columnIndex
            });
        },

        _renderCellTemplate: function (metric, data, className, view, columnIndex) {
            const me = this;

            const showSourceIcons = me.dashboardModel.getCuration().isCustomDashboard;
            const tooltipTemplate = Marionette.TemplateCache.get('#tableCellTooltip');
            if (RivalIQ.metric.property.isNumeric(metric.attributes)) {
                const rawValue = _.last(data);
                const tipValue = (_.isNumber(rawValue) ? metric.formatExpanded(data) : '--');

                const channel = metric.get('channel');
                let tooltip = tooltipTemplate({
                    name: metric.getName(),
                    value: tipValue,
                    channel: channel,
                    sourceIcon: (showSourceIcons ? RivalIQ.enums.sourceDetails[metric.getSource()].iconSvg : null)
                });

                let partialDataPopover = null;
                let elideMetric = false;
                const timeAnalysisMode = _.head(data);

                const calculationMetadata = metric.getCalculationMetadata(timeAnalysisMode);
                const missingSources = _.get(calculationMetadata, ['missingSources']);
                const mainPeriodMissingSources = _.get(metric.getCalculationMetadata(RivalIQ.enums.timeAnalysisMode.MAIN_PERIOD), ['missingSources']);

                // Show a warning unless it's the same warning we're showing for the main period (in which case, we don't want to show it as that would
                // result in triangles everywhere).
                if (!_.isEmpty(missingSources) && !_.isEqual(missingSources, mainPeriodMissingSources)) {
                    partialDataPopover = RivalIQ.utils.incompleteData.getIncompleteDataPopover('companyId', metric.get('companyId'), metric.build({
                        timeAnalysisMode: timeAnalysisMode
                    }), calculationMetadata);
                }

                if (!_.isEmpty(missingSources)) {
                    elideMetric = _.get(calculationMetadata, ['missingAllSources']) || (_.isNull(rawValue));
                }

                const privateDataNotConnectedTooltip = me._getPrivateDataNotConnectedTooltip(metric, metric.get('companyId'));
                if (privateDataNotConnectedTooltip) {
                    tooltip = privateDataNotConnectedTooltip;
                } else if (elideMetric) {
                    tooltip = _.get(partialDataPopover, ['popover', 'content']);
                    partialDataPopover = null;
                }

                const cellTemplateWithTip = Marionette.TemplateCache.get('#metricTableCellWithTipTemplate');
                return cellTemplateWithTip({
                    className: className,
                    value: (elideMetric ? '--' : metric.format(data)),
                    tooltip: tooltip,
                    partialDataPopover: partialDataPopover || null
                });
            }

            const fn = getCellTemplate(view, columnIndex);
            return fn({
                className: className,
                value: metric.format(data)
            });
        },

        renderCell: function (data, columnIndex, rowIndex) {
            const me = this;

            const timeAnalysisMode = _.head(data);

            const metric = me.getMetricModel(rowIndex, columnIndex);
            const isNumericMetric = RivalIQ.metric.property.isNumeric(metric.attributes);

            const selectedBenchmarkCompareAgainst = me.getProp('selectedBenchmarkCompareAgainst');
            let benchmarkCompareAgainst = null;
            if (selectedBenchmarkCompareAgainst && isNumericMetric && me.benchmarkMetrics) {
                const source = metric.get('source');
                if (source === RivalIQ.enums.sources.CROSS_CHANNEL || _.includes(RivalIQ.enums.sourceDetails[RivalIQ.enums.sources.CROSS_CHANNEL].subSources, source)) {
                    benchmarkCompareAgainst = (selectedBenchmarkCompareAgainst ? RivalIQ.apps.Overview.utils.getSelectedBenchmarkCompareAgainst(selectedBenchmarkCompareAgainst) : null);
                } else {
                    const hasMultiPrivateChannelAdvanced = RivalIQ.state.plan.get('has_multi_handle_private_advanced');
                    if (hasMultiPrivateChannelAdvanced && RivalIQ.enums.sourceDetails[source].privateData) {
                        benchmarkCompareAgainst = (selectedBenchmarkCompareAgainst ? RivalIQ.apps.Overview.utils.getSelectedBenchmarkCompareAgainst(selectedBenchmarkCompareAgainst) : null);
                    }
                }
            }

            if (columnIndex === 0) {
                const source = metric.get('source');

                const channelTemplate = Marionette.TemplateCache.get('#metricTableChannelCellTemplate');

                const fontIcon = RivalIQ.data.channels.get(source).get('squareFontIcon');
                let channelSvg = null;
                const showSourceIcons = me.dashboardModel.getCuration().isCustomDashboard;
                if (showSourceIcons) {
                    channelSvg = RivalIQ.data.channels.get(source).get('iconSvg');
                }

                const companyId = me.getProp('companyId');
                let partialDataPopover = null;
                const calculationMetadata = metric.getCalculationMetadata(RivalIQ.enums.timeAnalysisMode.MAIN_PERIOD);
                if (!_.isEmpty(_.get(calculationMetadata, ['missingSources']))) {
                    partialDataPopover = RivalIQ.utils.incompleteData.getIncompleteDataPopover('companyId', companyId, metric, calculationMetadata);
                }

                let benchmarkCompareAgainstName;
                let benchmarkPartialDataPopover;
                if (benchmarkCompareAgainst) {
                    const benchmarkCompanies = RivalIQ.apps.Overview.utils.getCompaniesForFilter(benchmarkCompareAgainst.benchmarkCompaniesFilter, me);

                    if (benchmarkCompanies.length === 1) {
                        benchmarkCompareAgainstName = benchmarkCompanies[0].get('name');
                    } else {
                        benchmarkCompareAgainstName = benchmarkCompareAgainst.name;
                    }

                    // warnings
                    const benchmarkMetric = me.getBenchmarkData(RivalIQ.enums.timeAnalysisMode.MAIN_PERIOD, columnIndex, rowIndex);
                    const calculationMetadata = benchmarkMetric.getMetadata().calculationMetadata;
                    const incompleteDataDetails = RivalIQ.utils.incompleteData.getBenchmarkIncompleteDataDetails(calculationMetadata, benchmarkMetric, benchmarkCompareAgainst);

                    benchmarkPartialDataPopover = _.get(incompleteDataDetails, ['partialDataTooltip']);
                }

                return channelTemplate({
                    className: 'invert-table-cell text-overflow',
                    fontIcon: fontIcon,
                    channelSvg: channelSvg,
                    value: _.last(data),
                    tooltipBody: metric.getDescription(),
                    benchmarkCompareAgainstName: benchmarkCompareAgainstName,
                    partialDataPopover: partialDataPopover,
                    benchmarkPartialDataPopover: benchmarkPartialDataPopover
                });
            }

            let className = 'metric-table-cell';
            if (timeAnalysisMode === RivalIQ.enums.timeAnalysisMode.DELTA_RELATIVE) {
                className += ` ${RivalIQ.formatter.relativeMetricClass(_.last(data))}`;
            }

            if (benchmarkCompareAgainst && isNumericMetric) {
                const benchmarkMetric = me.getBenchmarkData(timeAnalysisMode, columnIndex, rowIndex);

                if (!_.isUndefined(benchmarkMetric)) {
                    if (timeAnalysisMode === RivalIQ.enums.timeAnalysisMode.MAIN_PERIOD) {
                        return me._renderBenchmarkCurrentCell(metric, data, benchmarkMetric);
                    } else {
                        return me._renderBenchmarkCompareCell(metric, data, benchmarkMetric, className, columnIndex);
                    }
                }
            }

            if (timeAnalysisMode === RivalIQ.enums.timeAnalysisMode.MAIN_PERIOD) {
                return me._renderSparkLineCell(metric, data, rowIndex);
            }

            return me._renderCellTemplate(metric, data, className, me, columnIndex, rowIndex);
        }
    });

    const CompanyTable = VerticalTable.mixin({

        mixins: {
            focusCompanyTitlePlacementMixin: App.mixins.FocusCompanyTitlePlacementMixin
        },

        view: CompanyTableView,

        hasFooter: _.constant(true),
        footerView: App.views.WarningLegendFooterView,

        processProps: function (props) {
            const me = this;

            _.defaults(props, {
                companyId: props.focusCompanyId,
                orderBy: 1,
                sortable: false,
                showDescriptions: true,
                customAbbreviations: true,
                nameDimensions: ['channel', 'timeAnalysisMode'],
                focusCompanyTitlePlacement: RivalIQ.enums.widgetFocusCompanyTitlePlacement.PREFIX
            });

            // if we'll be displaying half width, omit the trailing columns.
            props.columnLimit = (!me.isFocus() && props.columnWidth === 6 ? 3 : null);

            return VerticalTable.prototype.processProps.apply(me, arguments);
        },

        // Do not serialize companyId so that we always show the focus company
        getPropsForSerialization: function (props) {
            const me = this;
            props = RivalIQ.apps.Dashboard.widgets.ChartWidget.prototype.getPropsForSerialization.call(me, props);
            return _.omit(props, 'companyId', 'limit');
        },

        csvExportProps: function () {
            const me = this;

            const csvConfig = _.defaults({
                invert: true,
                includeDimensionColumn: true
            }, widgets.TableWidget.prototype.csvExportProps.call(me));

            // If the table is showing benchmark data, we want to create some extra metrics for the benchmark columns (which
            // need some magic in order to be appended to the rows for the focus company data).
            const selectedBenchmarkCompareAgainst = me.getProp('selectedBenchmarkCompareAgainst');
            if (selectedBenchmarkCompareAgainst) {
                const benchmarkCompareAgainst = RivalIQ.apps.Overview.utils.getSelectedBenchmarkCompareAgainst(selectedBenchmarkCompareAgainst);
                let metrics = csvConfig.metric;
                metrics = metrics.concat(_.map(metrics, function (metric) {
                    const benchmarkDimensions = _.without(metric.dimensions || [], 'companyId');
                    return _.defaults({
                        extraFreeDimensions: {
                            companyId: benchmarkCompareAgainst.name
                        },
                        useMatchingMetricColumn: true,

                        aggregate: benchmarkCompareAgainst.aggregate,
                        benchmarkCompaniesFilter: benchmarkCompareAgainst.benchmarkCompaniesFilter,
                        dimensions: benchmarkDimensions
                    }, metric);
                }));
                csvConfig.metric = metrics;
                csvConfig.includesBenchmarkColumns = true;
            }

            return csvConfig;
        }
    });

    /*
     * NOTE this is a separate widget b/c it allows us to save vertical tables
     * without serializing the focus company id
     */
    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.COMPANY_TABLE, CompanyTable);
});
